/**
 * Single source of truth for all API URL paths.
 * Paths are relative — the base URL (prefixUrl) is set on each Ky client.
 *
 * Dev:  Vite proxy rewrites /api → Cloud Run identity-service, and the
 *       /*-api aliases → Cloud Run platform-gateway (see vite.config.ts).
 * Prod: Firebase Hosting rewrites proxy /api/**, /workflow/v1/**, etc.
 *       to the respective Cloud Run services (same-origin).
 *
 * IMPORTANT: Paths must NOT start with "/" when used with Ky's prefixUrl.
 */

/**
 * The agent-name segment platform-gateway's `/agents/{agent}/**` proxy routes on.
 * Named once because four prompt-catalog paths share it, and because the segment is
 * the gateway's registered service key — not a slug this app is free to restyle.
 */
const DATA_SCORING_AGENT = 'data-scoring-agent';

export const endpoints = {
  auth: {
    login: () => 'auth/login',
    me: () => 'auth/me',
    refresh: () => 'auth/refresh',
    logout: () => 'auth/logout',
    changePassword: () => 'auth/change-password',
    exchangeCode: () => 'auth/exchange-code',
    google: () => 'auth/google',
    microsoft: () => 'auth/microsoft',
    switchSubsidiary: () => 'auth/switch-subsidiary',
    impersonate: () => 'auth/impersonate',
    stopImpersonate: () => 'auth/stop-impersonate',
    resendVerification: () => 'auth/resend-verification',
    verifyEmail: () => 'auth/verify-email',
    setPassword: () => 'auth/set-password',
    inviteStatus: () => 'auth/invite-status',
  },
  organizations: {
    list: () => 'organizations',
    byId: (id: string) => `organizations/${id}`,
    create: () => 'organizations',
    update: (id: string) => `organizations/${id}`,
    delete: (id: string) => `organizations/${id}`,
    subsidiaries: (id: string) => `organizations/${id}/subsidiaries`,
    /** Full-wizard commit — creates org + subsidiaries + products + admin in one call. */
    onboard: () => 'onboarding',
  },
  subsidiaries: {
    byId: (subsidiaryId: string) => `subsidiaries/${subsidiaryId}`,
    list: () => 'subsidiaries',
    search: () => 'subsidiaries/search',
    create: () => 'subsidiaries',
    update: (id: string) => `subsidiaries/${id}`,
    delete: (id: string) => `subsidiaries/${id}`,
    /** Subsidiary members (users in a subsidiary) */
    members: (subsidiaryId: string) => `subsidiaries/${subsidiaryId}/users`,
    inviteMember: (subsidiaryId: string) => `subsidiaries/${subsidiaryId}/users/invite`,
    addMember: (subsidiaryId: string) => `subsidiaries/${subsidiaryId}/users/add`,
    bulkAddMembers: (subsidiaryId: string) => `subsidiaries/${subsidiaryId}/users/add/bulk`,
    searchMembers: (subsidiaryId: string) => `subsidiaries/${subsidiaryId}/users/search`,
    updateMember: (subsidiaryId: string, userId: string) =>
      `subsidiaries/${subsidiaryId}/users/${userId}`,
    removeMember: (subsidiaryId: string, userId: string) =>
      `subsidiaries/${subsidiaryId}/users/${userId}`,
    /** Resend the invite/verification email to a member with an outstanding invite. */
    resendInvite: (subsidiaryId: string, userId: string) =>
      `subsidiaries/${subsidiaryId}/users/${userId}/resend-invite`,
    /** Enrolled products for a subsidiary. */
    products: (subsidiaryId: string) => `subsidiaries/${subsidiaryId}/products`,
    /** PATCH a single enrolled product's contract details. */
    updateProduct: (subsidiaryId: string, productCode: string) =>
      `subsidiaries/${subsidiaryId}/products/${productCode}`,
    /** Subsidiary health */
    health: () => 'subsidiaries/health',
    /** Data management */
    export: (subsidiaryId: string) => `subsidiaries/${subsidiaryId}/export`,
    importPreflight: (subsidiaryId: string) => `subsidiaries/${subsidiaryId}/import/preflight`,
    import: (subsidiaryId: string) => `subsidiaries/${subsidiaryId}/import`,
  },
  users: {
    list: () => 'users/list',
    search: () => 'users/search',
    invite: () => 'users/invite',
    create: () => 'users',
    update: (id: string) => `users/${id}`,
    byId: (id: string) => `users/${id}`,
    delete: (id: string) => `users/${id}`,
    superAdmin: (id: string) => `users/${id}/super-admin`,
  },
  rbac: {
    /** Roles */
    roles: () => 'rbac/roles',
    rolesSearch: () => 'rbac/roles/search',
    roleById: (id: string) => `rbac/roles/${id}`,
    rolePermissions: (id: string) => `rbac/roles/${id}/permissions`,
    /** Permissions */
    permissions: () => 'rbac/permissions',
    permissionsSearch: () => 'rbac/permissions/search',
    permissionById: (id: string) => `rbac/permissions/${id}`,
    permissionToggle: (id: string) => `rbac/permissions/${id}/toggle`,
    permissionsCheck: () => 'rbac/permissions/check',
    userPermissionsCheck: (userId: string) => `rbac/users/${userId}/permissions/check`,
    /** Resources */
    resources: () => 'rbac/resources',
    resourcesSearch: () => 'rbac/resources/search',
    resourceById: (id: string) => `rbac/resources/${id}`,
    resourceToggle: (id: string) => `rbac/resources/${id}/toggle`,
    resourcesByModule: () => 'rbac/resources/by-module',
    resourceModules: () => 'rbac/resources/modules',
    resourcePermissions: (resourceId: string) => `rbac/resources/${resourceId}/permissions`,
    resourcePermissionsBulk: (resourceId: string) =>
      `rbac/resources/${resourceId}/permissions/bulk`,
    resourcePermissionById: (permissionId: string) => `rbac/resources/permissions/${permissionId}`,
    resourcePermissionToggle: (permissionId: string) =>
      `rbac/resources/permissions/${permissionId}/toggle`,
  },
  screens: {
    navigation: () => 'screens/navigation',
    byModule: () => 'screens/by-module',
    create: () => 'screens',
    update: (id: string) => `screens/${id}`,
    delete: (id: string) => `screens/${id}`,
    bulkSort: () => 'screens/bulk-sort',
    /** Subsidiary screen access */
    subsidiary: (subsidiaryId: string) => `screens/subsidiary/${subsidiaryId}`,
    subsidiaryToggle: (subsidiaryId: string, screenId: string) =>
      `screens/subsidiary/${subsidiaryId}/${screenId}/toggle`,
    subsidiaryBulkUpdate: (subsidiaryId: string) =>
      `screens/subsidiary/${subsidiaryId}/bulk-update`,
    subsidiaryDelete: (subsidiaryId: string, screenId: string) =>
      `screens/subsidiary/${subsidiaryId}/${screenId}`,
    /** Export/import (move Screen definitions between environments) — SuperAdminGuard / SystemAdminGuard. */
    exportPreview: () => 'screens/export/preview',
    export: () => 'screens/export',
    importPreview: () => 'screens/import/preview',
    import: () => 'screens/import',
  },
  abac: {
    policies: () => 'abac/policies',
    policyById: (id: string) => `abac/policies/${id}`,
    attributesMe: () => 'abac/me/attributes',
    attributesByUser: (userId: string) => `abac/users/${userId}/attributes`,
    deleteAttribute: (userId: string, key: string) => `abac/users/${userId}/attributes/${key}`,
  },
  sso: {
    config: () => 'subsidiary/sso',
    delete: () => 'subsidiary/sso',
    oauthConfigure: () => 'subsidiary/sso/oauth-domains',
    oauthUpdate: () => 'subsidiary/sso/oauth-domains',
    oidcConfigure: () => 'subsidiary/sso/oidc',
    oidcUpdate: () => 'subsidiary/sso/oidc',
    samlConfigure: () => 'subsidiary/sso/saml',
    samlUpdate: () => 'subsidiary/sso/saml',
    disable: () => 'subsidiary/sso/disable',
    samlMetadata: () => 'subsidiary/sso/metadata',
    test: (subsidiaryId: string) => `admin/sso/${subsidiaryId}/test`,
  },
  chat: {
    create: () => 'chat/conversations',
    conversation: (id: string) => `chat/conversations/${id}`,
    send: (conversationId: string) => `chat/conversations/${conversationId}/messages`,
  },
  experiments: {
    list: () => 'experiments',
    byId: (id: string) => `experiments/${id}`,
    stats: () => 'experiments/stats',
    sampleProduct: () => 'experiments/sample-product',
    catalog: () => 'experiments/catalog',
    catalogFilters: () => 'experiments/catalog/filters',
    /** POST — creates a draft experiment on the experiment-service (different client). */
    create: () => 'experiments',
    /** PATCH — partial update of an existing draft (called between wizard steps). */
    update: (draftId: string) => `experiments/${draftId}`,
    /** DELETE — discard a draft from the listing page. */
    delete: (id: string) => `experiments/${id}`,
    /**
     * POST (multipart) — set or replace the candidate scope: a CSV of merchant
     * offer ids that narrows which products become experiment candidates. The
     * part name is `offerIdsFile`, and no `Content-Type` may be set by hand.
     * DELETE — clear it, back to filter-only selection. Both return the full
     * experiment DTO.
     *
     * A separate sub-resource because `update()` is JSON-only and cannot carry a
     * file. Editable until content scoring consumes the candidate pool, after
     * which both verbs answer 409 — the same window as `scopingFilters`.
     *
     * See `docs/experiment-candidate-scope.md`.
     */
    offerIds: (id: string) => `experiments/${id}/offer-ids`,
    /** POST — fire-and-forget kick-off of the content-scoring workflow for an experiment. */
    contentScoringTrigger: (id: string) => `experiments/${id}/content-scoring/trigger`,
    /** GET — current progress of the content-scoring workflow. Polled until terminal. */
    contentScoringStatus: (id: string) => `experiments/${id}/content-scoring/status`,
    /** GET — aggregate catalog + per-attribute quality scores from the latest scoring run. */
    contentScoringQualityScore: (id: string) => `experiments/${id}/content-scoring/quality-score`,
    /** GET — paginated per-product scoring results for the Enrichment Overview "Preview Products" tab. */
    contentScoringResults: (id: string) => `experiments/${id}/content-scoring/results`,
    /** POST — kick-off the sample-enrichment workflow (generates copy for ~10 sample products). Body optional. */
    sampleEnrichmentTrigger: (id: string) => `experiments/${id}/sample-enrichment/trigger`,
    /** GET — current progress of the sample-enrichment workflow. Polled until terminal. */
    sampleEnrichmentStatus: (id: string) => `experiments/${id}/sample-enrichment/status`,
    /** GET — Before/After/Lift + per-attribute + per-category quality scores for the latest sample run. */
    sampleEnrichmentQualityScore: (id: string) =>
      `experiments/${id}/sample-enrichment/quality-score`,
    /** GET — paginated per-product preview cards (original title + image + score + generated description) for the latest sample run. */
    sampleEnrichmentPreview: (id: string) => `experiments/${id}/sample-enrichment/preview`,
    /** POST — kick off the end-to-end experiment pipeline (split → curation → enrichment → delivery). Body empty. */
    pipelineTrigger: (id: string) => `experiments/${id}/pipeline/trigger`,
    /** GET — read-only forecast: how many candidates are persisted + PROCEED/WARN/BLOCK recommendation. Drives the trigger gate. */
    pipelinePreview: (id: string) => `experiments/${id}/pipeline/preview`,
    /** GET — current status of the experiment pipeline. No per-stage counters by design. Polled until terminal. */
    pipelineStatus: (id: string) => `experiments/${id}/pipeline/status`,
    /** GET — Before/After/Lift + per-attribute + per-category quality scores from the latest full-catalog pipeline run. Drives the Enrichment Overview page. */
    pipelineQualityScore: (id: string) => `experiments/${id}/pipeline/quality-score`,
    /** POST — record APPROVED/REJECTED decision while pipeline is paused at AWAITING_APPROVAL. One-shot (server returns 409 on the second call). */
    pipelineApproval: (id: string) => `experiments/${id}/pipeline/approval`,
    /** GET — paginated enriched products from the completed experiment pipeline run. Drives the Enrichment Overview "Preview Products" tab. */
    pipelineEnrichedProducts: (id: string) => `experiments/${id}/pipeline/enriched-products`,
    /** POST — kick off arm assignment for a CREATED experiment. Async job; returns status `ASSIGNING`. Body empty. */
    armAssignmentTrigger: (id: string) => `experiments/${id}/arm-assignment/trigger`,
    /** POST — operator-initiated stop. Body picks `finalStatus: CANCELLED | COMPLETED`. 409 once status is already terminal. */
    stop: (id: string) => `experiments/${id}/stop`,
    /** PATCH — **Archive / Unarchive** (List Archive API): sets or clears
     *  `archivedAt` so the experiment leaves / rejoins the default list. No
     *  status change; exactly reversible. Body: `{ archived: boolean }`. 409
     *  `{ error: { code: EXPERIMENT_WORK_IN_FLIGHT | ARCHIVE_STATE_CHANGED } }`
     *  while a sub-run executes. Response is the full row incl. attribution. */
    setArchived: (id: string) => `experiments/${id}/archived`,
    /** POST — bulk archive / unarchive. Body `{ ids: string[] (1–100), archived }`.
     *  Always 200 when well-formed; read `summary` + `results[].outcome`. The
     *  literal `:` is part of the path. */
    setArchivedBatch: () => 'experiments/archived:batch',
  },
  /**
   * Scheduled enrichment triggers — Meta Ads (and future connectors).
   * Scoped by connector + sync + filters rather than experimentId.
   * All paths are under the experiment service's `/workflow/v1` prefix
   * (use `experimentsClient`).
   */
  notEnriched: {
    /** GET — counts of products earlier batches curated but never enriched; POST — the same
     *  under a catalog scope (`syncId` + `filters` in the body). */
    summary: (subsidiaryId: string) => `subsidiaries/${subsidiaryId}/not-enriched`,
    /** GET — keyset page of those products (`after` / `nextCursor`); POST — under a scope. */
    products: (subsidiaryId: string) => `subsidiaries/${subsidiaryId}/not-enriched/products`,
  },
  scheduledEnrichment: {
    /** POST — create or return an existing scheduled-job record for the session. */
    jobs: () => 'enrichments/scheduled/jobs',
    /** POST — kick off content-scoring for a connector sync. Returns workflowId. */
    contentScoringTrigger: () => 'enrichments/content-scoring/trigger',
    /** POST — kick off sample-enrichment for a connector sync. Returns workflowId. */
    sampleEnrichmentTrigger: () => 'enrichments/sample-enrichment/trigger',
    /** POST — kick off the full-catalog pipeline for a connector sync. Returns workflowId. */
    pipelineTrigger: () => 'enrichments/pipeline/trigger',
    /** GET — poll status of any scheduled trigger workflow by workflowId. */
    triggerStatus: (wfId: string) => `enrichments/content-scoring/${wfId}/status`,
    /** GET — aggregate quality scores once content-scoring COMPLETED. */
    contentScoringQualityScore: (wfId: string) =>
      `enrichments/${wfId}/content-scoring/quality-score`,
    /** GET — Before/After/Lift quality scores once sample-enrichment COMPLETED. */
    sampleEnrichmentQualityScore: (wfId: string) =>
      `enrichments/${wfId}/sample-enrichment/quality-score`,
    /** GET — paginated Before/After product cards from a completed sample-enrichment run. */
    sampleEnrichmentPreview: (wfId: string) => `enrichments/${wfId}/sample-enrichment/preview`,
    /** GET — paginated enriched products from the completed pipeline run. */
    pipelineEnrichedProducts: (wfId: string) => `enrichments/${wfId}/pipeline/enriched-products`,
    /** GET — Before/After/Lift quality scores from the completed Meta pipeline run. Drives the Enrichment Overview agent panel. */
    pipelineQualityScore: (wfId: string) => `enrichments/${wfId}/pipeline/quality-score`,
    /** POST — record APPROVED/REJECTED decision while the Meta pipeline is paused at AWAITING_APPROVAL. */
    pipelineApproval: (wfId: string) => `enrichments/pipeline/${wfId}/approval`,
    /** GET — full job status snapshot including all three sub-workflow states and pipeline stages. */
    jobStatus: (jobId: string) => `enrichments/scheduled/jobs/${jobId}/status`,
    /** GET reads / PATCH edits a scheduled job's stored run config (edits are recorded).
     *  `jobId` is the enrichment-job UUID, not the `scheduled-job-…` workflow id. 409 while
     *  any phase of the job is still running. */
    jobRunConfig: (jobId: string) => `enrichments/jobs/${jobId}/config`,
    /** GET — run-config change history for the job, newest first, capped at 100. */
    jobRunConfigHistory: (jobId: string) => `enrichments/jobs/${jobId}/config/history`,
    /** POST — batch-fetch per-product tags and trends for a list of product ids
     *  within a given enrichment job. Used to hydrate the Tags / Trends columns
     *  in the catalog-operations products table. */
    productTagsTrends: () => 'enrichments/products/tags-trends',
    /** POST — batch-fetch per-product enriched/not-enriched flags for a list of
     *  product ids within a given enrichment job. Drives the "Not Enriched"
     *  badge on the catalog-operations products list. */
    productEnrichmentStatus: () => 'enrichments/products/enrichment-status',
    /** GET — per-product Meta scoring detail for the drill-down modal.
     *  Returns 404 when no row exists for the given productId + stage. */
    metaProductScoreDetails: (wfId: string, productId: string) =>
      `enrichments/${wfId}/meta/products/${productId}/score-details`,
  },
  /**
   * Analytics service — read-only experiment dashboards (use `analyticsClient`).
   * Mirrors the routes shipped by OneLookApp/agentic-analytics PRs #7-#11.
   */
  analytics: {
    experiments: {
      list: () => 'experiments',
      summary: (id: string) => `experiments/${id}/summary`,
      timeline: (id: string) => `experiments/${id}/timeline`,
      /** GET — per-arm cumulative lift trend for the Performance tab chart. `?metric=` selects one of the 8 lift metrics (see `PerformanceMetric`); no grain/sensitivity params. */
      performanceTrend: (id: string) => `experiments/${id}/performance-trend`,
      health: (id: string) => `experiments/${id}/health`,
      agentSummary: (id: string) => `experiments/${id}/agent-summary`,
      exportCsv: (id: string) => `experiments/${id}/export.csv`,
      armComparison: (id: string) => `experiments/${id}/arm-comparison`,
      roasComparison: (id: string) => `experiments/${id}/roas-comparison`,
      dailyLift: (id: string) => `experiments/${id}/daily-lift`,
      /** Per-category revenue-weighted DiD lift table (AN-025). */
      categoryPerformance: (id: string) => `experiments/${id}/category-performance`,
      /** GET — list of downloadable CSV report tiles for the category-mode Detailed Data Downloads grid. */
      dataDownloads: (id: string) => `experiments/${id}/data-downloads`,
      /** GET — streams a single report's CSV. `reportType` is the tile's `id` from `dataDownloads`. */
      dataDownloadCsv: (id: string, reportType: string) =>
        `experiments/${id}/data-downloads/${reportType}.csv`,
      /** Health detail panels: daily in-stock trend, pre/post exclusions table, adjusted metrics. */
      healthInStock: (id: string) => `experiments/${id}/health/in-stock`,
      healthCategoryExclusions: (id: string) => `experiments/${id}/health/category-exclusions`,
      healthAdjustedMetrics: (id: string) => `experiments/${id}/health/adjusted-metrics`,
      /** GET — per-arm A/B/C summary + lift-vs-control report (JSON). 422 while the assignment isn't yet computable. */
      armAssignmentReport: (id: string) => `experiments/${id}/arm-assignment/report`,
      /** GET — assigned products (title/image/bucket/segment/outlier). Filter by `arm` (repeatable) + `outliers`. */
      armAssignmentProducts: (id: string) => `experiments/${id}/arm-assignment/products`,
      /** GET — CSV export of the full test/control split (blob download). */
      armAssignmentExportCsv: (id: string) => `experiments/${id}/arm-assignment/export.csv`,
      /** GET — customer-shareable Experiment Design recap as a .docx (blob download). */
      armAssignmentDesignDoc: (id: string) => `experiments/${id}/arm-assignment/design-doc.docx`,
      /**
       * GET — every metric each arm carries, one row per (arm, metric), with its comparison
       * against the base arm (blob download). Optional `subsidiary_name` names the file only.
       * 422 when the assignment can't be recomputed.
       */
      armAssignmentAllMetricsCsv: (id: string) =>
        `experiments/${id}/arm-assignment/all-metrics.csv`,
      /** GET — 28-day results readout as a self-contained 9-slide HTML deck (blob download). */
      readoutDeck: (id: string) => `experiments/${id}/readout-deck.html`,
      /** GET — weekly difference-in-differences trend, one row per test-arm week (blob download). */
      didTrendCsv: (id: string) => `experiments/${id}/did-trend.csv`,
    },
  },
  /**
   * Trial workflow — catalog preflight verification and import trigger.
   * Uses `integrationsClient` (platform-gateway `/api/v1/integration` prefix).
   * Exact endpoint shapes must be confirmed with the backend team before first call.
   */
  trial: {
    /** POST (multipart) — validates the catalog file without importing; returns product list + stats. */
    preflight: (subsidiaryId: string) => `trial/subsidiaries/${subsidiaryId}/import/preflight`,
    /** POST (multipart) — triggers the actual trial catalog import; returns workflowId. */
    import: (subsidiaryId: string) => `trial/subsidiaries/${subsidiaryId}/import`,
    /** GET — polls the import workflow status (may share the onboarding polling endpoint). */
    importStatus: (workflowId: string) => `onboarding/import/${workflowId}/status`,
  },
  /**
   * Trial enrichment flow (v2) — a trial is now an enrichment_job.
   * All paths use `experimentsClient` (`/experiments-api` dev / `/workflow/v1` prod).
   * Trial ids are opaque strings (`trial-job-<uuid>`) — never parse as UUID.
   */
  trialEnrichment: {
    /** POST — create a new trial; returns TrialResponseDto with the String id. */
    create: () => 'trials',
    /** GET — fetch trial by id; use currentStage to route a returning user. */
    get: (id: string) => `trials/${id}`,
    /** POST — kick off Stage 1 content scoring; body optional (maxProducts etc.). */
    contentScoringTrigger: (id: string) => `trials/${id}/content-scoring/trigger`,
    /** GET — poll Stage 1 status until COMPLETED or FAILED. */
    contentScoringStatus: (id: string) => `trials/${id}/content-scoring/status`,
    /** POST — kick off Stage 2 sample enrichment after content scoring completes. */
    sampleEnrichmentTrigger: (id: string) => `trials/${id}/sample-enrichment/trigger`,
    /** GET — poll Stage 2 status until COMPLETED or FAILED. */
    sampleEnrichmentStatus: (id: string) => `trials/${id}/sample-enrichment/status`,
    /** GET — fetch quality score data once Stage 2 completes. */
    qualityScore: (id: string) => `trials/${id}/sample-enrichment/quality-score`,
    /** GET — paginated per-product preview cards after Stage 2 completes. */
    sampleEnrichmentPreview: (id: string) => `trials/${id}/sample-enrichment/preview`,
    /** POST — kick off Stage 3 full-catalog enrichment. */
    fullCatalogTrigger: (id: string) => `trials/${id}/full-catalog/trigger`,
    /** GET — poll Stage 3 status until AWAITING_APPROVAL or FAILED. */
    fullCatalogStatus: (id: string) => `trials/${id}/full-catalog/status`,
    /** GET — fetch before/after quality scores once Stage 3 reaches AWAITING_APPROVAL. */
    fullCatalogQualityScore: (id: string) => `trials/${id}/full-catalog/quality-score`,
    /** GET — paginated enriched product preview after Stage 3 reaches AWAITING_APPROVAL. */
    fullCatalogEnrichedProducts: (id: string) => `trials/${id}/full-catalog/enriched-products`,
  },
  /**
  /**
   * GCS-backed file upload flow (presign → client PUT → acknowledge).
   * All endpoints use `integrationsClient` (platform-gateway integration prefix).
   * Base path: /api/v1/integration-service/uploads
   */
  uploads: {
    /** POST — obtain a GCS resumable upload URL; no DB record created yet. */
    presign: () => 'uploads/presign',
    /** POST — confirm GCS upload complete; creates Upload row and starts processing. Returns id = syncId. */
    acknowledge: () => 'uploads/acknowledge',
    /** GET — fetch the Upload row by id (= syncId). */
    get: (id: string) => `uploads/${id}`,
    /** GET — the file behind an upload (a feed audit's importRefId): metadata,
     *  audit type and every revision. */
    file: (id: string) => `uploads/${id}/file`,
    /** POST — signed, logged download URL for one revision. Body: `{ revision?, variant? }`. */
    downloadUrl: (id: string) => `uploads/${id}/download-url`,
    /** GET — paginated download history (issuance, not completion). Query: page, size. */
    downloads: (id: string) => `uploads/${id}/downloads`,
    /** DELETE — cancel the upload workflow and mark row CANCELLED. */
    cancel: (id: string) => `uploads/${id}`,
    /** GET — quality summary written by the processing workflow. Requires COMPLETED status. */
    summary: (id: string) => `uploads/${id}/summary`,
    /**
     * GET — stateless blob preview (no Upload row). Query params: gcs_blob_name, file_format, max_rows.
     * Different from preview(id) below which reads from the BQ external table post-processing.
     */
    blobPreview: () => 'uploads/preview',
    /** GET — top-N rows preview from the bronze table scoped to this upload. */
    preview: (id: string) => `uploads/${id}/preview`,
    /** POST — filtered, sorted, paginated rows from the bronze table for this upload. */
    rows: (id: string) => `uploads/${id}/rows`,
    /** POST — paginated list of uploads for the subsidiary. */
    list: () => 'uploads/list',
    /** POST — stateless column-mapping preview for a GCS blob (no Upload row created). */
    previewMapping: () => 'uploads/preview-mapping',
    /** POST — stateless dry-run Shopify→GMC conversion for a GCS blob (creates
     *  no Upload, writes no blob, mutates no connection). Real converter
     *  output — coverage, gate failures, warnings — for QA before it's ever
     *  wired into the real upload flow. */
    shopifyExportConvert: () => 'uploads/shopify-export:convert',
    /** GET — list blobs already in the subsidiary's connector-scoped upload lane
     *  (Entry B of single-file delivery). Query param: `connector_code`. */
    browse: () => 'uploads/browse',
    /** GET — combined live + persisted delivery status for a delivery-routed upload.
     *  Poll until `delivery.stage == "awaiting_approval"`, then again to `completed`. */
    deliveryStatus: (id: string) => `uploads/${id}/delivery-status`,
    /** POST — approve a delivery-routed upload with an optional committed filter;
     *  triggers SFTP delivery. Body: `{ appliedFilters?, allowEmpty? }`. */
    approve: (id: string) => `uploads/${id}/approve`,
    /** POST — reject a delivery-routed upload. Body: `{ reason }`. */
    reject: (id: string) => `uploads/${id}/reject`,
    /** GET — column names + inferred datatypes of a staged feed (Meta delivery review grid). */
    headers: (id: string) => `uploads/${id}/headers`,
    /** GET — which fields of a staged feed are filterable (upload-scoped filter catalog). */
    filters: (id: string) => `uploads/${id}/filters`,
  },
  /**
   * Multi-file upload groups (`/upload-groups/*`). Gated server-side by
   * `upload_groups_enabled` (404 when off). In the single-file (Phase 1) flow only
   * the group-scoped filter catalog + preview are used — via the N=1 `uploadGroupId`
   * returned from `POST /uploads/acknowledge`. Wire format is camelCase both ways.
   */
  uploadGroups: {
    /** POST — create a group in INITIALIZED for a connection. GET — paginated list. */
    root: () => 'upload-groups',
    /** GET — full group detail incl. children + metrics. */
    get: (id: string) => `upload-groups/${id}`,
    /** POST — mint a resumable URL for a new child file (creates an Upload row). */
    presignFile: (id: string) => `upload-groups/${id}/files/presign`,
    /** POST — confirm a child file's GCS PUT completed. Returns the full group. */
    acknowledgeFile: (id: string) => `upload-groups/${id}/files/acknowledge`,
    /** DELETE — remove a child file (INITIALIZED groups only). */
    deleteFile: (id: string, uploadId: string) => `upload-groups/${id}/files/${uploadId}`,
    /** POST — start the delivery workflow → INGESTING. */
    start: (id: string) => `upload-groups/${id}/start`,
    /** GET — the connector's row-level filter catalog for this group. */
    filters: (id: string) => `upload-groups/${id}/filters`,
    /** GET — filter-aware preview of the group's ingested bronze rows.
     *  Query params: `filters` (url-encoded JSON), `page`, `size`. */
    preview: (id: string) => `upload-groups/${id}/preview`,
    /** POST — paginated filter-aware rows (dict shape) for larger filter payloads. */
    rows: (id: string) => `upload-groups/${id}/rows`,
    /** POST — approve the group with an optional committed filter; triggers delivery. */
    approve: (id: string) => `upload-groups/${id}/approve`,
    /** POST — reject the group with a required reason. */
    reject: (id: string) => `upload-groups/${id}/reject`,
  },
  /**
   * Integration-service onboarding status — aggregated channel-connection +
   * catalog-import state for the post-login redirect gate. Served by
   * platform-gateway under the `/api/v1/integration` prefix (use `integrationsClient`).
   */
  integration: {
    onboardingStatus: () => 'onboarding/status',
    /** POST — paginated, filterable list of integration pipelines (extraction /
     *  ingestion / delivery). Consumed by the disconnect-error fallback to
     *  detect when a connection is still wired into an active pipeline. */
    pipelinesList: () => 'pipelines/list',
  },
  /**
   * Analytics sync — kicks off a connector data pull (async Temporal workflow).
   * Uses `integrationsClient` (integration-service prefix). Body is snake_case;
   * `connection_id` in the body, not the URL.
   */
  analyticsSync: {
    trigger: () => 'analytics/sync',
  },
  /** Google Ads Keyword Planner (read + resync). Body is camelCase for these
   *  endpoints; connectionId is a required URL path param on all three
   *  (connection-scoped contract — not present in the request body). */
  keywords: {
    list: (connectionId: string) => `connections/${connectionId}/keywords/list`,
    detail: (connectionId: string) => `connections/${connectionId}/keywords/detail`,
    resync: (connectionId: string) => `connections/${connectionId}/keywords/resync`,
  },
  connections: {
    create: () => 'connections',
    byId: (id: string) => `connections/${id}`,
    update: (id: string) => `connections/${id}`,
    delete: (id: string) => `connections/${id}`,
    list: () => 'connections/list',
    connected: () => 'connections/connected',
    /** POST — pause an active connection. Used as the recovery path when a
     *  DELETE is rejected with INTEGRITY_ERROR because a pipeline is still
     *  bound to the connection. */
    pause: (id: string) => `connections/${id}/pause`,
    /** POST — resume a previously paused connection, flipping it back to
     *  ACTIVE so downstream syncs continue. Mirrors `pause`. */
    resume: (id: string) => `connections/${id}/resume`,
    /** GET/POST/DELETE — the connection's recurring Temporal Schedule. GET returns
     *  `state: MISSING` for connectors that never register one. */
    schedule: (id: string) => `connections/${id}/schedule`,
    /** POST — pause/resume the *schedule* (distinct from pausing the connection). */
    schedulePause: (id: string) => `connections/${id}/schedule/pause`,
    scheduleResume: (id: string) => `connections/${id}/schedule/resume`,
    /** POST — fire the scheduled workflow once, immediately, without waiting. */
    scheduleTrigger: (id: string) => `connections/${id}/schedule/trigger`,
    /** GET — every scheduled job in the subsidiary, with upcoming run times.
     *  Two-segment path: a bare `connections/schedules` would be shadowed by
     *  the `connections/{id}` route. */
    schedulesList: () => 'connections/schedules/list',
    /** POST — skip only the next occurrence, leaving the cadence intact.
     *  Append `?type=coverage` to target the coverage schedule. Answers 409 for
     *  cron/calendar-driven schedules, which cannot be skipped this way. */
    scheduleSkipNext: (id: string) => `connections/${id}/schedule/skip-next`,
    /** POST — run the GMC audit now (`?force`, `?lookback_days`); 202 `{ scheduleId, workflowId }`.
     *  Pulls the catalog itself, then records GMC's per-product verdict (BE PR #703). */
    audit: (id: string) => `connections/${id}/audit`,
    validate: (id: string) => `connections/${id}/validate`,
    /** GET — the connection's currently-active pipeline workflow (e.g. an
     *  in-flight catalog ingestion). 404s when nothing is running; lets the
     *  onboarding hub resume an import in progress instead of restarting. */
    activeWorkflow: (id: string) => `connections/${id}/active-workflow`,
    /** GET — file-upload destinations for an existing connection, resolved by its
     *  own handler. Connector-agnostic counterpart to
     *  `connectors.uploadTargets`, which is addressed by a bare merchant id and
     *  is therefore GMC-shaped. 400s for connectors whose handler doesn't set
     *  `supports_upload_delivery`. */
    uploadTargets: (id: string) => `connections/${id}/upload-targets`,
    /** GET — primary data sources a GMC connection can pin ingestion to
     *  (`?accountId=` selects a sub-account on an MCA). Read-only; the pin
     *  itself is two `config` keys on the ordinary PATCH. 400s for connectors
     *  without feed selection — callers hide the picker on that. */
    primaryFeeds: (id: string) => `connections/${id}/primary-feeds`,
    /** GET — every supplemental data source on a GMC connection's account (API and
     *  FILE input) with feed label + content language: the per-locale destination
     *  picker behind `config.locale_destinations`. 400s for other connectors. */
    supplementalDataSources: (id: string) => `connections/${id}/supplemental-data-sources`,
    /** GET/PUT — the `shopify_export` config block (store_domain, currency,
     *  brand_fallback + advanced overrides) a GMC connection needs to convert
     *  a Shopify CSV export. PUT replaces the whole block — read-modify-write. */
    shopifyExportConfig: (id: string) => `connections/${id}/shopify-export-config`,
    /** POST — list files/directories on a file-transport connection (SFTP, GCS).
     *  Body: `{ path?, pattern?, recursive?, page?, size? }`. Stage 2 of the
     *  SFTP extraction wizard: the operator picks the directory the exports land
     *  in. Connector-agnostic — the connection's own transport answers. */
    browse: (id: string) => `connections/${id}/browse`,
    /** POST — read the first rows of ONE file on a file-transport connection.
     *  Body: `{ path, max_rows?, expectedHeaders? }` (mixed casing on the wire). Stage 3 of the wizard:
     *  confirm the file parses before mapping a single column. */
    preview: (id: string) => `connections/${id}/preview`,
    /** POST — check every typed `config.files` entry (SFTP/FTP) over one session.
     *  Body (camelCase): `{ files?: [{ type, path, fileName?, pattern?,
     *  expectedHeaders? }] }` — omit `files` to check what is saved. */
    filesValidate: (id: string) => `connections/${id}/files/validate`,
    /** Stages 4-6 of the SFTP extraction wizard (`config.extractionMode='SFTP'`),
     *  where an operator maps a delivered export's headers onto bronze columns.
     *  Generic `/connections/{id}/...` surface — the connector-specific parts are
     *  resolved server-side through handler hooks, so these are not Adobe-only
     *  paths even though ADOBE_ANALYTICS is the first connector to implement them. */
    extraction: {
      /** GET — wizard progress: selected directory, each configured report's
       *  mapping, and `blockers` (what still prevents ingestion). `isReady` is
       *  false until every configured report has a VALIDATED mapping, which is
       *  also what holds the connection at PENDING rather than ACTIVE. */
      status: (id: string) => `connections/${id}/extraction/status`,
      /** POST — read a real file's headers and get back a proposed
       *  `{source header → bronze column}` map, every column the report accepts,
       *  and sample rows. Proposal only; nothing is persisted. Works before any
       *  mapping is saved — pass `pattern`/`fileName` from the file picked while
       *  browsing. */
      suggestMapping: (id: string) => `connections/${id}/extraction/mapping/suggest`,
      /** PUT — persist one report's file config + column map (body carries
       *  `reportKey`). Saving ALWAYS clears the validation stamp, so an edited
       *  mapping must be re-validated before it can ingest. */
      mapping: (id: string) => `connections/${id}/extraction/mapping`,
      /** DELETE — forget one report: its pattern, column map and validation
       *  stamp, and its place in the ingested selection. Saving can only
       *  overwrite a mapping, so this is the only way a report set up by
       *  mistake (or one the tenant stopped sending) leaves the list. */
      deleteMapping: (id: string, reportKey: string) =>
        `connections/${id}/extraction/mapping/${encodeURIComponent(reportKey)}`,
      /** POST — dry-run the saved mapping over real rows, and stamp it when it
       *  holds. Returns the rows exactly as they would be written to bronze.
       *  This is the gate: until it passes, ingestion refuses the report. */
      validate: (id: string) => `connections/${id}/extraction/validate`,
      /** GET/PUT — which reports this connection ingests. GET lists all nine with
       *  per-report `isConfigured`/`isValidated`/`isEnabled` for the picker. PUT
       *  `{ reports: null }` goes back to deriving the set (in SFTP mode: every
       *  validated report); `{ reports: [...] }` pins an explicit subset. */
      reports: (id: string) => `connections/${id}/extraction/reports`,
    },
    /** GET — Adobe Analytics report suites the connection's stored credential
     *  can reach, for the suite picker. API extraction only: it mints an IMS
     *  token and calls Adobe, so an SFTP-extraction connection (which may hold
     *  no Adobe credential at all) cannot answer it. 400s for a non-Adobe
     *  connector. */
    adobeReportSuites: (id: string) => `connections/${id}/adobe-analytics/report-suites`,
    ga4Properties: (id: string) => `connections/${id}/ga4/properties`,
    ga4DataStreams: (id: string, propertyId: string) =>
      `connections/${id}/ga4/properties/${propertyId}/data-streams`,
    /** Account-level metrics from the connector (e.g. GMC totals + issues). */
    metrics: (id: string) => `connections/${id}/metrics`,
    /** POST — aggregate live health check across every connection in the
     *  subsidiary. Returns per-connection `isValid` used to badge the Connect
     *  Platforms cards. */
    health: () => 'connections/health',
    /** POST — live health check for a single connection, with a per-component
     *  breakdown. Fetched lazily when the user hovers the unhealthy heart. */
    healthById: (id: string) => `connections/${id}/health`,
    /** POST — paginated list of a connection's processed catalog images
     *  (POST-list convention: `{ filters, pagination }` in body). */
    catalogImagesList: (id: string) => `connections/${id}/catalog-images/list`,
    /** POST — fire-and-forget reprocess of a scoped set of catalog images.
     *  Body carries exactly one selector: syncId | externalIds | failedOnly. */
    catalogImagesReprocess: (id: string) => `connections/${id}/catalog-images/reprocess`,

    // -- Meta delivery workspace (integration-service UI contract, 2026-09-10) --
    /** POST — dry-run a connector's credentials before creating a connection row. */
    test: () => 'connections/test',
    /** GET — which resources/actions the connector behind a connection supports;
     *  used to hide UI the connector cannot do. */
    capabilities: (id: string) => `connections/${id}/capabilities`,
    /** POST — turn enrichment output into a Meta-shaped TSV staged for review.
     *  Body (snake_case): `{ workflow_id?, external_ids?, limit? }`. Response is
     *  snake_case too (`upload_id`, `row_count`, `gcs_blob_name`) — the one
     *  exception on this surface; see `meta-delivery.service.ts`. */
    supplementalFeedGenerate: (id: string) => `connections/${id}/supplemental-feed:generate`,
    /** GET — latest coverage snapshot (how complete the catalog data is). */
    coverage: (id: string) => `connections/${id}/coverage`,
    /** GET — coverage over time, for a trend view. */
    coverageHistory: (id: string) => `connections/${id}/coverage/history`,
    /** GET — per-product coverage drill-down. Coverage v3 filters: `state`, `reason`,
     *  `field`, `search` (+ `page`/`size`); the legacy `segment` still works but is no
     *  longer needed. Reads the latest finished coverage run only. */
    coverageProducts: (id: string) => `connections/${id}/coverage/products`,
    /** GET — what to do next: ranked, read-time insights over the latest `coverage_daily`
     *  row, each with the products it covers and an optional action. */
    coverageInsights: (id: string) => `connections/${id}/coverage/insights`,
    /** POST — recompute coverage on demand. */
    coverageTrigger: (id: string) => `connections/${id}/coverage/trigger`,
    /** POST — start (or join) the coverage redeliver job of the latest finished coverage run;
     *  GET — that job's status (404 `REDELIVER_JOB_NOT_FOUND` when it has none). */
    coverageRedeliver: (id: string) => `connections/${id}/coverage/redeliver`,
    /** GET — status of one coverage redeliver job of this connection. */
    coverageRedeliverJob: (id: string, jobId: string) =>
      `connections/${id}/coverage/redeliver/${encodeURIComponent(jobId)}`,
    /** POST — audit trail for one connection (POST-list convention: filter body). */
    eventsList: (id: string) => `connections/${id}/events/list`,

    // -- Google Ads interactive OAuth connect (advance mode), GOOGLE_ADS only --
    /** POST — mint the Google consent-screen URL for a `PENDING` connection
     *  created without a `refresh_token`. `redirect_uri` is a query param, not
     *  a body field. NOTE: this response's `data` is a plain dict
     *  (`oauth_url` / `connection_id`, snake_case) — the one exception to the
     *  camelCase envelope every other endpoint on this surface uses. */
    oauthStart: (id: string) => `connections/${id}/oauth/start`,
    /** POST — exchange the Google-redirect `code` for tokens. For advance mode
     *  this activates the connection outright; for connect mode it stays
     *  `PENDING` (an ad account still needs to be picked — see
     *  `googleAdsCustomers` below). `code` + `redirect_uri` are query params.
     *  Response is a normal camelCase `ConnectionResponse`. */
    oauthCallback: (id: string) => `connections/${id}/oauth/callback`,
    /** GET — connect-mode only: the Google Ads accounts the signed-in user can
     *  access, once step 4 (oauth/callback) has completed. Normal camelCase
     *  fields (`customerId`/`descriptiveName`/`currencyCode`/`timeZone`/
     *  `manager`) — no snake_case exception here, unlike oauth/start. `data`
     *  is `{ connectionId, customers: [...] }`, NOT a bare array — verified
     *  against the live response 2026-09-28. */
    googleAdsCustomers: (id: string) => `connections/${id}/google-ads/customers`,
  },
  /** Shareable connect links (integration-service, authenticated). A workspace
   *  user mints a tokenised `/connector/:token` URL for an external Google Ads
   *  owner. The raw URL is returned once on create/resend only. */
  connectLinks: {
    create: () => 'connect-links',
    /** POST-list convention: `{ filters, pagination }` in body. */
    list: () => 'connect-links/list',
    revoke: (id: string) => `connect-links/${id}`,
    /** POST — mint a fresh token for the same request; the old one is revoked. */
    resend: (id: string) => `connect-links/${id}/resend`,
  },
  /** Google OAuth grants created through connect links — one refresh token per
   *  Google identity per workspace, shared by that grant's connections. */
  grants: {
    list: () => 'grants/list',
    byId: (id: string) => `grants/${id}`,
    /** DELETE — revoke at Google and disconnect every connection on the grant. */
    revoke: (id: string) => `grants/${id}`,
    /** POST — mint a `purpose=reauth` link that reruns consent for this grant. */
    reauthLink: (id: string) => `grants/${id}/reauth-link`,
  },
  /** Public connect-link routes (no JWT) — called through
   *  `publicIntegrationsClient` from the `/connector/:token` pages. */
  publicConnectLinks: {
    lookup: (token: string) => `public/connect-links/${encodeURIComponent(token)}`,
    authorize: (token: string) => `public/connect-links/${encodeURIComponent(token)}/authorize`,
    customers: (token: string) => `public/connect-links/${encodeURIComponent(token)}/customers`,
    complete: (token: string) => `public/connect-links/${encodeURIComponent(token)}/complete`,
    /** Backend OAuth callback; reached by full-page navigation from the public redirect page. */
    oauthCallback: () => 'public/oauth/google-ads/callback',
  },
  /** Aggregate backend health. `all` is a single gateway endpoint that fans out
   *  to every downstream service + agent server-side and returns their statuses
   *  (it also cold-starts min-instances=0 services). Issued via
   *  `integrationsClient`, so it rides the same dev-proxy / Firebase rewrite that
   *  routes `/api/v1/integration/**` to the gateway. Consumed by the
   *  Integrations → Health page and the post-login wake-up. */
  serviceHealth: {
    all: () => 'health/all',
    /** GET — probe/wake one service by id (platform service or agent name). */
    service: (id: string) => `health/service/${id}`,
  },
  /** Data Lake browser (integration-service). Issued via `integrationsClient`,
   *  so `datalake/*` rides the same `/api/v1/integration/**` rewrite the gateway
   *  maps to `/api/v1/integration-service/datalake/*`. */
  datalake: {
    /** GET — one level of the tree; `?path=` selects the folder (empty = root). */
    browse: () => 'datalake/browse',
    /** POST — short-lived (15 min) signed GET URL for a file's raw bytes.
     *  Body: `{ source, subTree, path }`. */
    signedUrl: () => 'datalake/signed-url',
    /** POST — GCS resumable upload session for dropping a file into the lake.
     *  Drop-only: no Upload row, no ingestion workflow. The destination is
     *  fixed server-side; the body carries only a filename. */
    uploadUrl: () => 'datalake/upload-url',
    /** DELETE — hard-delete one file from `uploads/manual-uploads/`. Super-admin
     *  only, and scoped to that folder server-side. Encoded: the filename comes
     *  from live GCS object names. */
    deleteManualUpload: (filename: string) =>
      `datalake/manual-uploads/${encodeURIComponent(filename)}`,
  },
  /** Performance-signal recompute (integration-service). Issued via
   *  `integrationsClient`, so `enrichment-insights/*` rides the same
   *  `/api/v1/integration/**` rewrite the gateway maps to
   *  `/api/v1/integration-service/enrichment-insights/*`.
   *
   *  Re-runs the EnrichmentInsightsWorkflow for a batch and rewrites the
   *  persisted signal rows the Performance Signals tab reads. Fire-and-poll:
   *  the POST hands back a run id, `recomputeStatus` reports the Temporal
   *  workflow's terminal state, and the caller then re-reads
   *  `catalogOperations.batchInsights`. */
  enrichmentInsights: {
    /** POST — starts a recompute; responds with the new `runId`. Body is
     *  camelCase: `{ workflowId, connectorCode }`, where `connectorCode` is the
     *  long curation-API form (`GOOGLE_MERCHANT`), not the listing's `GMC`. */
    recompute: () => 'enrichment-insights/recompute',
    /** GET — Temporal state for one recompute. Accepts the bare `runId` or the
     *  full `EnrichmentInsightsWorkflow-<runId>` id. `RUNNING` is the only
     *  non-terminal state. 404 is deliberately ambiguous — unknown id, aged-out
     *  Temporal retention (7–30 days), or another subsidiary's run — so it can
     *  never confirm a run exists. */
    recomputeStatus: (runId: string) => `enrichment-insights/recompute/${runId}`,
  },
  /** Coverage-card recompute (integration-service). Exact twin of
   *  `enrichmentInsights` above — same `integrationsClient`, same
   *  `/api/v1/integration/**` rewrite, same fire-and-poll contract — but it
   *  rewrites the batch-scoped coverage rows the Coverage Signals section
   *  reads, not the cumulative insight rows.
   *
   *  Note the split of concerns: coverage is *read* from workflow-service
   *  (`catalogOperations.batchCoverage`) and *recomputed* here. The read has no
   *  recompute of its own because the rows are produced by integration-service. */
  enrichmentCoverage: {
    /** POST — starts a recompute; responds with the new `runId`. Body is
     *  camelCase `{ workflowId, connectorCode }`, where `connectorCode` is the
     *  long form (`GOOGLE_MERCHANT`), not the listing's `GMC`. Subsidiary comes
     *  from the `X-Subsidiary-ID` header.
     *
     *  422 before anything is scheduled when the connector has no coverage
     *  field registry (GOOGLE_MERCHANT / META_COMMERCE today) or the subsidiary
     *  has no ACTIVE connection for it — a permanent "no coverage here", not a
     *  transient error to retry. */
    recompute: () => 'enrichment-coverage/recompute',
    /** GET — Temporal state for one recompute. Accepts the bare `runId` or the
     *  full `EnrichmentCoverageWorkflow-<runId>` id. `RUNNING` is the only
     *  non-terminal state. 404 is deliberately ambiguous — unknown id, aged-out
     *  retention, or another subsidiary's run. */
    recomputeStatus: (runId: string) => `enrichment-coverage/recompute/${runId}`,
  },
  /** Post-enrichment validation (integration-service, `integrationsClient`), keyed by the run's
   *  root id (`pipelineWorkflowId ?? workflowId`) + `?connectorCode=`. Re-enrich: `workflowRuns`. */
  enrichmentValidation: {
    /** GET — the banner: status (`NONE`/`RUNNING`/`COMPLETED`/`FAILED`) + counts.
     *  A never-validated run is `200 status: NONE`; `503` = verdict store down. */
    summary: (workflowId: string) =>
      `enrichment-validation/${encodeURIComponent(workflowId)}/summary`,
    /** GET — "View products": latest verdict per product. Query: `status`
     *  (`invalid`|`warning`|`repairable`|`all`), `q`, `rule`, `field`, `reenrich`, `sort`,
     *  `page` (1-based), `size` ≤ 100. */
    products: (workflowId: string) =>
      `enrichment-validation/${encodeURIComponent(workflowId)}/products`,
    /** GET — the drawer's filter-menu counts (`rules`, `fields`, `reenrich`) under `status` + `q`. */
    productFacets: (workflowId: string) =>
      `enrichment-validation/${encodeURIComponent(workflowId)}/products/facets`,
    /** POST — counts (never ids) over the latest verdicts of the `productIds` sent (≤ 5 000):
     *  passed / invalid / fixableInvalid / warning / notValidated. */
    productsSummary: (workflowId: string) =>
      `enrichment-validation/${encodeURIComponent(workflowId)}/products-summary`,
    /** POST — one list of validated products across runs: `scopes` (run + its product ids,
     *  ≤ 50 runs, ≤ 20 000 ids) plus the per-run list's filters, sort and page. */
    productsSearch: () => 'enrichment-validation/products/search',
    /** POST — the cross-run list's filter-menu counts and per-tab `statusCounts`. */
    productsSearchFacets: () => 'enrichment-validation/products/search/facets',
    /** POST — run a FULL validation of stored content now (no body; a recompute in any state but
     *  RUNNING). 404 not this tenant's run · 409 running or a scores-only run · 503 down. */
    revalidate: (workflowId: string) =>
      `enrichment-validation/${encodeURIComponent(workflowId)}/revalidate`,
  },
  /** Validated-products export of one enrichment run, built by **agentic-export-service** from
   *  the `enrichmentValidation.products` read (`exportClient`, prefix `/api/v1/export`). Query:
   *  the drawer's `status`, `q`, `rule`, `field`, `sort`, `connectorCode`, plus `format`
   *  (`csv`|`tsv`). One row per normalised field (before / after). Filename rides
   *  `Content-Disposition`, counts ride `X-Export-Product-Count` / `X-Export-Row-Count`;
   *  over the row cap the server answers 413 `{ totalElements, maxRows }`. */
  enrichmentValidationExport: {
    products: (workflowId: string) =>
      `enrichment-validation/${encodeURIComponent(workflowId)}/products`,
  },
  /** Read-only ClickHouse table browser (integration-service). Super-admin
   *  only; targets the configured analytics DB (`bronze_dev`). Table/column
   *  segments are encoded — they come from live ClickHouse identifiers. */
  clickhouse: {
    /** GET — every table in the configured database with size estimates. */
    tables: () => 'clickhouse/tables',
    /** GET — a table's columns + per-column filter metadata. */
    schema: (table: string) => `clickhouse/tables/${encodeURIComponent(table)}/schema`,
    /** POST — filtered/sorted/paginated rows. Body: `ChRowsRequest`. */
    rows: (table: string) => `clickhouse/tables/${encodeURIComponent(table)}/rows`,
    /** GET — distinct values for a column (multi-select filter population). */
    distinct: (table: string, column: string) =>
      `clickhouse/tables/${encodeURIComponent(table)}/columns/${encodeURIComponent(column)}/distinct`,
    /** POST — CSV export of the filtered/sorted view. Body: `ChExportRequest`. */
    export: (table: string) => `clickhouse/tables/${encodeURIComponent(table)}/export`,
  },
  /** Config-table exports — also on the export service (`exportClient`), but
   *  reading the agentic-config Postgres rather than ClickHouse. The table is a
   *  body field rather than a path segment, so nothing needs encoding here. */
  configTables: {
    /** POST — CSV/TSV export of a filtered config table, written to GCS and
     *  returned as a signed URL. Body: `TableExportRequestApi` — the same filter
     *  payload agentic-config's `POST /tables/rows` takes. */
    export: () => 'config/tables/export',
  },
  /** Unified data operations through a connection. */
  sync: {
    /** List products from a connection (transformed, cursor-paginated). */
    productsList: () => 'sync/products/list',
    /** POST — write capabilities (update/delete + max batch size) for a connection. */
    capabilities: () => 'sync/capabilities',
    /** POST — push a single product update back to the source connection. */
    productsUpdate: () => 'sync/products/update',
    /** POST — delete a single product from the source connection by external id. */
    productsDelete: () => 'sync/products/delete',
  },
  /** Config service — KPI catalogue + content-attribute taxonomy used by step-4 of the wizard. */
  config: {
    /** Platform-scoped KPI list (e.g. `?platform=google`). */
    kpis: (platform: string) => `kpis?platform=${encodeURIComponent(platform)}`,
    /** Hierarchical content-attribute taxonomy. */
    contentAttributes: () => 'content-attributes',
    /** Subsidiary-scoped customer constraints (target audience, markets, languages). */
    customerConstraints: () => 'customer-constraints',
    /** Subsidiary-scoped preferred and restricted vocabulary terms. */
    restricted: () => 'restricted',
    /** Subsidiary-scoped brand preferences (channel overrides, brand name usage, guidelines doc). */
    brandPreferences: () => 'brand-preferences',
    /** Upload / fetch / delete the brand guidelines document (global or per-platform). */
    guidelinesDocument: () => 'brand-preferences/guidelines-document',
    /** Subsidiary-scoped sync schedule per product (Item Setup, Product Copy, SEO/AEO). */
    syncSchedule: () => 'sync-schedule',
  },
  /** Catalog/data-quality product sync workflows. */
  products: {
    /** Trigger a GMC sync workflow — pulls products, scores, writes to ClickHouse. */
    sync: () => 'products/sync',
    /** POST — paginated sync history for a connection (id + date + product count
     *  + avg quality score). Drives the sync picker on the Catalog Images tab. */
    syncs: () => 'products/syncs',
    /** Grouped variants summary for a connection — drives step 1 of the experiment wizard. */
    variants: (connectionId: string) => `products/${connectionId}/variants`,
    /** Parent + children detail for a single `matchValue` (group) under a connection. */
    variantDetail: (connectionId: string, matchValue: string) =>
      `products/${connectionId}/variants/${encodeURIComponent(matchValue)}`,
    /** Paginated, filterable product list for a connection — drives step 2 of the experiment wizard. */
    list: (connectionId: string) => `products/${connectionId}/list`,
    /**
     * Same list, but the id set arrives as an uploaded FILE rather than inline
     * filter values — `multipart/form-data` with `file`, an optional
     * `idsFilterKey` (defaults `offer_id`), and an optional `filters` part
     * holding the JSON body `list()` would have taken. No id-count cap, so this
     * is the route for a list too large for `list()`'s 4,000-value budget.
     * Response is identical to `list()`.
     */
    listByIdsFile: (connectionId: string) => `products/${connectionId}/list/by-ids-file`,
    /** Filter-by-filter waterfall explaining how the catalog reduces to the `/list` eligible total.
     *  POST the same filters object sent to `list` (minus pagination/sortBy). Drives the step-2 eligibility popover. */
    eligibilityBreakdown: (connectionId: string) =>
      `products/${connectionId}/eligibility-breakdown`,
    /** Column metadata for a given connector — label, type, sortable/filterable flags. */
    columns: (connectorCode: string) =>
      `products/columns?connectorCode=${encodeURIComponent(connectorCode)}`,
    /** GET — identifier fields (offer id, item group id, MPN, GTIN, title) the list `search` can target. */
    searchFields: (connectorCode: string) =>
      `products/search-fields?connectorCode=${encodeURIComponent(connectorCode)}`,
    /** Dynamic filter definitions (options + types) scoped to a connection/connector. POST with filter state. */
    filters: () => 'products/filters',
    /** Paginated, searchable distinct option values for a single high-cardinality filter key. POST with key + pagination. */
    filterOptions: () => 'products/filters/options',
    /** GET — platform product catalog grouped by category; drives the org-onboarding enrollment step. */
    catalog: () => 'products/catalog',
    /** GET — deterministic fields/prompts (+ grouping options for `type=variants`) for the AI product-filter panels. */
    aiFilterSuggestions: (connectionId: string, type: 'list' | 'variants') =>
      `products/${connectionId}/ai-filter/suggestions?type=${type}`,
    /** POST — stateless conversational AI product filter on the FLAT list; send full message history each turn. Returns a filter + matched products or a clarification. */
    aiFilter: (connectionId: string) => `products/${connectionId}/ai-filter/list`,
    /** POST — same conversational filter on the GROUPED variants view; returns matching unique-product groups (drives Step 1). */
    aiFilterVariants: (connectionId: string) => `products/${connectionId}/ai-filter/variants`,
    /** GET — full connector-shaped detail for a single product under a connection. */
    detail: (connectionId: string, externalId: string) =>
      `products/${connectionId}/${encodeURIComponent(externalId)}`,
    /** GET — default grouping-method options for a connector (Products page). */
    defaultGroupingConfig: (connectorCode: string) =>
      `products/default-grouping-config?connectorCode=${encodeURIComponent(connectorCode)}`,
    /** POST — category breakdown for a connection (count/quality per category). */
    categories: (connectionId: string) => `products/${connectionId}/categories`,
  },
  /**
   * Data Quality — the Integrations → Data Quality dashboard. The backend mounts
   * the data-quality router under `/products/...`; the sync trigger reuses
   * `products.sync` and status polling reuses `workflows.status`. Issued via
   * `integrationsClient` (`/api/v1/integration` prefix); snake_case on the wire.
   */
  dataQuality: {
    /** POST — connection-level quality summary (KPIs, grades, dimensions) for a sync. */
    summary: (connectionId: string) => `products/${connectionId}/summary`,
    /** POST — richer overview payload (top lists, attention items, filter options). */
    overview: (connectionId: string) => `products/${connectionId}/overview`,
    /** POST — top field-level quality issues aggregated across the catalog. */
    issues: () => 'products/issues',
    /** POST — per-sync quality trend points over a date window. */
    trends: () => 'products/trends',
    /**
     * GET — full quality scorecard for a single product under a connection.
     * The external id is emitted RAW (not percent-encoded): connector ids such
     * as GMC resource names contain slashes, and the backend captures the id as
     * a path so raw `/` matches its route. Encoding to `%2F` makes the gateway
     * (Google Frontend) reject the path with a 404.
     */
    productDetail: (connectionId: string, externalId: string) =>
      `products/${connectionId}/${externalId}`,
    /** GET — per-sync quality history for a single product (raw external id, see productDetail). */
    productHistory: (connectionId: string, externalId: string) =>
      `products/${connectionId}/${externalId}/history`,
    /** POST — per-product score delta between two syncs (Compare Syncs page). */
    comparison: () => 'products/comparison',
  },
  /** Temporal workflow management (status, pause, resume, cancel, reset). */
  workflows: {
    /** Poll a workflow's live status + progress by workflow_id. */
    status: (workflowId: string) => `workflows/${workflowId}/status`,
    /** POST — graceful cancel of a running workflow. 500s when the run has
     *  already finished, so callers must surface the message rather than assume
     *  success. */
    cancel: (workflowId: string) => `workflows/${workflowId}/cancel`,
    /** POST — pause a running workflow at the next safe boundary (catalog import:
     *  the page in progress finishes first). */
    pause: (workflowId: string) => `workflows/${workflowId}/pause`,
    /** POST — resume a paused workflow. */
    resume: (workflowId: string) => `workflows/${workflowId}/resume`,
    /** POST — restart a workflow from scratch. 400 for workflow types the
     *  backend cannot reconstruct arguments for. */
    restart: (workflowId: string) => `workflows/${workflowId}/restart`,
    // -- Read-only Temporal inspection (integration-service, BE PR #686) ------
    // Every route takes an optional `runId` query param (defaults to the
    // current run) and 404s once the execution ages out of Temporal retention.
    /** GET — one page of event history (`pageSize`, `nextPageToken`, `afterEventId`, `order`). */
    history: (workflowId: string) => `workflows/${encodeURIComponent(workflowId)}/history`,
    /** GET (SSE) — live tail of new history events (`afterEventId`). */
    historyStream: (workflowId: string) =>
      `workflows/${encodeURIComponent(workflowId)}/history/stream`,
    /** GET — execution detail: input, result/failure, pending activities, attributes. */
    describe: (workflowId: string) => `workflows/${encodeURIComponent(workflowId)}/describe`,
    /** GET — child workflows started by this run (`includeLiveStatus`). */
    children: (workflowId: string) => `workflows/${encodeURIComponent(workflowId)}/children`,
    /** GET — pollers on the workflow's own task queue. */
    workers: (workflowId: string) => `workflows/${encodeURIComponent(workflowId)}/workers`,
    /** GET — live call stack; 409 when the workflow isn't running. */
    stackTrace: (workflowId: string) => `workflows/${encodeURIComponent(workflowId)}/stack-trace`,
    /** GET — query handlers the workflow declares. */
    queries: (workflowId: string) => `workflows/${encodeURIComponent(workflowId)}/queries`,
    /** POST — run one query. Body `{ args: unknown[] }` (≤ 20 args, ≤ 64 KiB). */
    query: (workflowId: string, queryName: string) =>
      `workflows/${encodeURIComponent(workflowId)}/queries/${encodeURIComponent(queryName)}`,
  },
  connectors: {
    list: () => 'connectors/list',
    byCode: (code: string) => `connectors/${code}`,
    /** GET — placeholders + rules for a connector's templated output file name
     *  (SFTP only; others 404). `?template=` validates and renders one name. */
    fileNaming: (code: string) => `connectors/${code}/file-naming`,
    /** Default grouping-method options for a given connector (step 1 of the create-experiment wizard). */
    defaultGroupingConfig: (connectorCode: string) =>
      `products/default-grouping-config?connectorCode=${encodeURIComponent(connectorCode)}`,
    /** GET — file-upload destinations for a connector, addressed by a direct
     *  merchant id (pre-connection). Used during onboarding to pick the SFTP
     *  target filename before a connection exists. Pass `merchantId` via
     *  searchParams. */
    uploadTargets: (code: string) => `connectors/${code}/upload-targets`,
    /** GET — pre-connection counterpart of `connections.supplementalDataSources`,
     *  addressed by `merchantId` (searchParams) for the create flow. */
    supplementalDataSources: (code: string) => `connectors/${code}/supplemental-data-sources`,
  },
  /**
   * Integration pipelines — CRUD + run history for the Integrations → Pipelines
   * page. Issued via `integrationsClient` (platform-gateway `/api/v1/integration`
   * prefix). `list` duplicates `integration.pipelinesList()` (kept for the
   * disconnect-error fallback); this group is the pipelines-feature surface.
   * Pipeline routers serialise snake_case, so services convert at the boundary.
   */
  pipelines: {
    /** POST — paginated, filterable list of pipelines. */
    list: () => 'pipelines/list',
    /** GET — single pipeline detail. */
    byId: (id: string) => `pipelines/${id}`,
    /** PATCH — update a pipeline (name/description/status/config/schedule). */
    update: (id: string) => `pipelines/${id}`,
    /** GET — paginated run history for a pipeline (`?page&size`). */
    runs: (id: string) => `pipelines/${id}/runs`,
    /** GET — the most recent run for a pipeline (404 → none yet). */
    latestRun: (id: string) => `pipelines/${id}/runs/latest`,
    /** GET — a single run by id. */
    runById: (runId: string) => `pipeline-runs/${runId}`,
    /** GET — lifecycle/status events for a run (`?skip&limit`). */
    runEvents: (runId: string) => `pipeline-runs/${runId}/events`,
    /** GET — ingest run chain (original + resumes/restarts) `runId` belongs to,
     *  oldest → newest. camelCase on the wire. */
    runLineage: (runId: string) => `pipeline-runs/${encodeURIComponent(runId)}/lineage`,
    /** GET — per-page ledger of an ingest run (`?status&offset&limit`, limit ≤ 200). */
    runPages: (runId: string) => `pipeline-runs/${encodeURIComponent(runId)}/pages`,
    /** POST — start a new ingest run from a FAILED/CANCELLED one. Body
     *  `{ mode: 'REMAINING' | 'RESTART' }`; 202 → new run/workflow ids; 409 + `code`. */
    resumeRun: (runId: string) => `pipeline-runs/${encodeURIComponent(runId)}/resume`,
  },
  /**
   * Deliveries — the integration-service Delivery API (`integrationsClient`).
   * Two surfaces share this group: the older read-only analytics reads
   * (summary/jobs/jobProducts, snake_case on the wire, converted at the service,
   * backing the delivery branch of the pipeline detail page) and the newer
   * run-centric console + trigger endpoints (deliver/dryRun + `runs/*`), which
   * are camelCase both ways and consumed directly with no case conversion.
   * `runId` is a `pipeline_run` UUID, encoded so it stays path-safe.
   */
  deliveries: {
    /** POST — aggregate delivery stats for a connection. */
    summary: () => 'deliveries/summary',
    /** POST — paginated delivery jobs for a connection. */
    jobs: (connectionId: string) => `deliveries/${connectionId}/jobs`,
    /** POST — paginated products within a delivery job. */
    jobProducts: (connectionId: string, jobId: string) =>
      `deliveries/${connectionId}/jobs/${jobId}/products`,
    /** GET — one product's full audit row within a job. */
    jobProductDetail: (connectionId: string, jobId: string, productId: string) =>
      `deliveries/${connectionId}/jobs/${jobId}/products/${productId}`,
    /**
     * POST — subsidiary-scoped DELIVERY pipeline runs that came from a manual file
     * upload, with the delivered file path(s) and a coarse `outcome`. Selected by
     * `upload_id IS NOT NULL` server-side, so it never includes enrichment-sourced or
     * API deliveries. camelCase both ways. Body: `{ filters?, pagination? }`.
     */
    supplementalRuns: () => 'deliveries/supplemental-runs/list',

    // -- Trigger (camelCase; DELIVERIES·CREATE) --------------------------------
    /** POST — trigger a delivery for a connection. Body: `TriggerDeliveryRequest`. */
    deliver: (connectionId: string) => `deliveries/${connectionId}/deliver`,
    /** POST — same as deliver but skips the destination push (`isDryRun`). */
    dryRun: (connectionId: string) => `deliveries/${connectionId}/dry-run`,

    /** POST — read-only breakdown of what a trigger would read (`DeliveryPlanRequest`). */
    plan: (connectionId: string) => `deliveries/${connectionId}/plan`,
    /** POST — the Breakdown grid: products of a plan with verdicts, search, filters and a cursor. */
    planProducts: (connectionId: string) => `deliveries/${connectionId}/plan/products`,
    /** GET — the fields a delivery to this connection can send (the connector's field registry). */
    deliverableFields: (connectionId: string) => `deliveries/${connectionId}/deliverable-fields`,
    /** POST — one product's detail: what would be sent and how it differs from the last delivery. */
    planProductDetail: (connectionId: string) => `deliveries/${connectionId}/plan/products/detail`,
    /** POST — start a multi-batch delivery job (2-20 batches, in order). */
    batchJobs: (connectionId: string) => `deliveries/${connectionId}/batch-jobs`,
    /** GET — one batch job's state + per-batch runs. */
    batchJob: (connectionId: string, jobId: string) =>
      `deliveries/${connectionId}/batch-jobs/${encodeURIComponent(jobId)}`,
    /** POST — stop a batch job after the current run. */
    batchJobCancel: (connectionId: string, jobId: string) =>
      `deliveries/${connectionId}/batch-jobs/${encodeURIComponent(jobId)}/cancel`,
    /** GET — newest run per source batch on a connection (`?connectionId&sourceWorkflowIds=…`). */
    latestBySource: () => 'deliveries/runs/latest-by-source',

    // -- Run-centric console (camelCase; `/deliveries/runs`) -------------------
    /** GET — paginated list of delivery-classified runs (`?family&status&…`). */
    runs: () => 'deliveries/runs',
    /** GET — full detail for one run. */
    run: (runId: string) => `deliveries/runs/${encodeURIComponent(runId)}`,
    /** GET — run → work-unit tree (`?includeProducts&productStatus&depth`). */
    runTree: (runId: string) => `deliveries/runs/${encodeURIComponent(runId)}/tree`,
    /** GET — paginated work units for a run (`?page&size`). */
    runUnits: (runId: string) => `deliveries/runs/${encodeURIComponent(runId)}/units`,
    /** GET — product leaves for one work unit (cursor page). */
    runUnitProducts: (runId: string, unitIndex: number) =>
      `deliveries/runs/${encodeURIComponent(runId)}/units/${String(unitIndex)}/products`,
    /** GET — product leaves across every unit in the run (cursor page).
     *  Returns EVERY product by default (BE PR #626); `?outcomes=` narrows to a
     *  set, `?excludeOutcomes=DELIVERED` reproduces the old needs-attention view. */
    runProducts: (runId: string) => `deliveries/runs/${encodeURIComponent(runId)}/products`,
    /** GET — the outcome filter menu for the leaf endpoints: static options with
     *  a `label`, one `isDefault`, and the `query` each forwards. Source the
     *  dropdown from this rather than an enum — unknown outcomes are a 422. */
    productOutcomes: () => 'deliveries/runs/product-outcomes',
    /** POST — batch by-id read (≤1000 ids): each product's latest audit row on a
     *  connection plus a `notFound` list. Body: `{ ids, idType, deliveryJobId?, includePayload? }`. */
    productsLookup: (connectionId: string) => `deliveries/${connectionId}/products/lookup`,
    /** POST — cause-grouped worklist of blocked products on a connection (BE PR
     *  #628). Body: `{ limit?, filters? }`. A product is "blocked" by outcome
     *  (FAILED/VALIDATION_FAILED/EXCLUDED) or ingest status
     *  (WITHHELD/ROW_REJECTED/TRANSPORT_FAILED) — positive evidence only. */
    blockedCauses: (connectionId: string) => `deliveries/${connectionId}/blocked/causes`,
    /** POST — drill into one cause's blocked products. Body: `{ cause, filters?, pagination? }`. */
    blockedProducts: (connectionId: string) => `deliveries/${connectionId}/blocked/products`,
    /** GET (CSV stream) — blocked products as a downloadable file. Query:
     *  `?cause&deliveryJobId&since&includeMock`. Formula-injection characters in
     *  cell values are apostrophe-escaped server-side. */
    blockedExport: (connectionId: string) => `deliveries/${connectionId}/blocked/export`,
    /** GET — did this job's failures get fixed in a later run? Query:
     *  `?resolution=all|resolved|unresolved&includeMock&limit&offset`. "Resolved"
     *  = first success strictly after this job, not merely delivered-by-now. */
    jobReconciliation: (connectionId: string, jobId: string) =>
      `deliveries/${connectionId}/jobs/${encodeURIComponent(jobId)}/reconciliation`,
    /** GET — chronological stage-trace timeline. */
    runTimeline: (runId: string) => `deliveries/runs/${encodeURIComponent(runId)}/timeline`,
    /** GET — retry-chain lineage with per-node recovery projection. */
    runLineage: (runId: string) => `deliveries/runs/${encodeURIComponent(runId)}/lineage`,
    /** GET — run report (totals/timing/failures/per-unit). */
    runReport: (runId: string) => `deliveries/runs/${encodeURIComponent(runId)}/report`,
    /** GET — outcome-bucket reconciliation (`?includeClickhouse`). */
    runReconcile: (runId: string) => `deliveries/runs/${encodeURIComponent(runId)}/reconcile`,
    /** POST — retry only the failed products into a new linked run. */
    runRetryFailed: (runId: string) => `deliveries/runs/${encodeURIComponent(runId)}/retry-failed`,
    /** POST — pause a run at the next page boundary (DELIVERIES·UPDATE). */
    runPause: (runId: string) => `deliveries/runs/${encodeURIComponent(runId)}/pause`,
    /** POST — resume a paused run (DELIVERIES·UPDATE). */
    runResume: (runId: string) => `deliveries/runs/${encodeURIComponent(runId)}/resume`,
    /** POST — cancel a run (DELIVERIES·UPDATE). */
    runCancel: (runId: string) => `deliveries/runs/${encodeURIComponent(runId)}/cancel`,
    /** GET (SSE) — live progress for one run. Read via `integrationsSseBaseUrl`,
     *  not the Ky client: raw fetch so `text/event-stream` isn't buffered. */
    runStream: (runId: string) => `deliveries/runs/${encodeURIComponent(runId)}/stream`,
    /**
     * GET — every delivery job one product appeared in on a connection, newest
     * first, with the full audit row per job (`?idType&limit&offset&includePayload`).
     * `productId` is the retailer id / SKU by default — URL-encoded because SKUs
     * routinely contain `/` or `#`. Not the standard paginated envelope: returns
     * `{ total, truncated, entries }`; 404 = no history at all, 503 = store down.
     */
    productTimeline: (connectionId: string, productId: string) =>
      `deliveries/${connectionId}/products/${encodeURIComponent(productId)}/timeline`,
  },
  /**
   * Configuration Manager — paths under the `/api/v1/config` prefix served by
   * platform-gateway → agentic-config (NOT the identity-service). Use
   * `configClient`, not `apiClient`. Platform identity is sent via the
   * `X-Platform-ID` header (UUID resolved from `/platforms`), not a query string.
   */
  configuration: {
    /** Platform catalogue — used to resolve a name like 'GMC' → its UUID. */
    platforms: () => 'platforms',
    /** Parameters grouped by category for the X-Platform-ID. */
    parameters: () => 'parameters',
    /** Category list with parameter counts for the X-Platform-ID. */
    parameterCategories: () => 'parameters/categories',
    /** Single parameter detail (no platform header required). */
    parameterById: (id: string) => `parameters/${id}`,
    /** Paginated audit log (filterable). For per-parameter history use the
     *  `parameter_ids` query filter. */
    auditTrail: () => 'audit-trail',
    /** Filter options { parameters, users } for the audit-trail UI. */
    auditTrailFilters: () => 'audit-trail/filters',
    /** Subsidiary-scoped parameters — same grouped-by-category shape as
     *  `/parameters`, resolved (override or inherited default) for the
     *  X-Subsidiary-ID header. Requires X-Platform-ID like the global list. */
    subsidiaryParameters: () => 'subsidiary-parameters',
    /** Single subsidiary parameter — GET for detail, PUT to set an override,
     *  DELETE to clear it (revert to the global default). */
    subsidiaryParameterById: (id: string) => `subsidiary-parameters/${id}`,
    /** Subsidiary-scoped audit trail — replaces `/audit-trail` for
     *  subsidiary edits. `page` selects which config surface's log to read
     *  (`'parameters'` here); filter further with `definition_ids`/`actions`/dates. */
    configAuditTrail: () => 'config-audit-trail',
    /** Treatment Configuration — current arm count + treatment-per-arm + full-catalog
     *  production treatment for the current platform (X-Platform-ID). GET returns
     *  current state; PUT replaces it. */
    treatmentConfig: () => 'treatment-config',
    /** Treatment catalogue. Platform-agnostic — no X-Platform-ID required. */
    treatments: () => 'treatments',
    /** Subsidiary-scoped Treatment Configuration — singleton resolved for the
     *  active subsidiary (X-Subsidiary-ID): override if set, else the
     *  platform default. GET/PUT/DELETE all target this same path (no id —
     *  one record per subsidiary+platform). */
    subsidiaryTreatmentConfig: () => 'subsidiary-treatment-config',
    /** Generic table-query pair (agentic-config PR #89). Both take the table as
     *  a body field (`table_name`), resolved against a server-side registry, so
     *  a newly registered table needs no change here. `columns` describes a
     *  table and the values its filter dropdowns should offer; `rows` returns
     *  filtered/sorted/paginated rows in the shared `PaginatedEnvelope`.
     *  Platform-agnostic reference data, so no X-Platform-ID.
     *  Contract: `agentic-config/docs/tables-api.md`. */
    tableColumns: () => 'tables/columns',
    tableRows: () => 'tables/rows',
    /** Ask AI over the *same* registry (agentic-config PR #90), so it can never
     *  reach a table `tableRows` cannot. `tableAiFilter` is one conversational
     *  turn — plain English in, a `tables/rows`-shaped filter plus a preview out;
     *  `tableAiSuggestions` is the deterministic field catalogue + starter prompts
     *  the panel opens with (no model call, and notably never a 503, so it is not
     *  a probe for whether Ask AI is configured).
     *  Contract: `agentic-config/docs/tables-ai-api.md`. */
    tableAiFilter: () => 'tables/ai-filter',
    tableAiSuggestions: () => 'tables/ai-filter/suggestions',
    /** Dashboard catalog with current platform's enabled state. */
    dashboards: () => 'dashboards',
    /** Per-dashboard toggle endpoint. PUT with `{ enabled, modified_by }`. */
    dashboardById: (id: string) => `dashboards/${id}`,
    /** Subsidiary-scoped dashboards — same catalog shape as `/dashboards`,
     *  resolved (override or platform default) for the active subsidiary. */
    subsidiaryDashboards: () => 'subsidiary-dashboards',
    /** Single subsidiary dashboard — GET for detail, PUT to set an override,
     *  DELETE to clear it (revert to the platform default). */
    subsidiaryDashboardById: (id: string) => `subsidiary-dashboards/${id}`,
  },
  /**
   * Enrichment Limits — global (no platform/subsidiary scope) numeric limits
   * with a bounded, audited envelope (agentic-config PR #94). Served by
   * platform-gateway → agentic-config under the `/api/v1/config` prefix, so use
   * `configClient`. Keys (e.g. `SCORING_MAX`) are migration-owned — the UI can
   * only read a limit and change its current value or reset it to default.
   */
  enrichmentLimits: {
    /** GET — list every limit with its current value + `[hard_min, hard_max]` envelope. */
    list: () => 'enrichment-limits',
    /** PUT — set a limit's current value (422 outside the envelope). DELETE — reset to default. */
    byKey: (key: string) => `enrichment-limits/${key}`,
    /** GET — paginated history of every change to a limit's value. */
    auditTrail: () => 'enrichment-limits/audit-trail',
  },
  /**
   * Catalog cost-approval gate settings — one global row in agentic-config
   * (`configClient`). GET always 200 (defaults until first save); PUT is a full
   * replace that 422s on invalid values.
   */
  costApprovalSettings: {
    /** GET — current settings. PUT — replace them (audited). */
    settings: () => 'cost-approval-settings',
    /** GET — paginated, newest-first history of every settings save. */
    history: () => 'cost-approval-settings/history',
  },
  /** agentic-config — the signed-in user's own UI preferences (needs the user token). */
  myPreferences: {
    /** GET — current preferences (defaults until saved). PUT — partial update. */
    detail: () => 'me/preferences',
  },
  /**
   * Catalog-Operations / enrichments service — paths under the
   * `/workflow/v1/enrichments` prefix served by platform-gateway. Use
   * `catalogOpsClient`, not `apiClient`. Detail endpoints key on the job
   * uuid (`id` from the listing response), not the human-readable batchId.
   */
  /**
   * Catalog-Operations workflow trigger — unified endpoint for kicking off
   * import/enrich/score/deliver pipelines (currently used by the Google Ads
   * full-catalog generation flow). Use `catalogOperationsClient`, not
   * `catalogOpsClient` (that one is the read-side `/workflow/v1/enrichments`
   * jobs listing — a different backend resource despite the similar name).
   */
  catalogOperationsWorkflow: {
    /** POST — kick off a catalog-operations pipeline run (e.g. GMC import → enrich → score → deliver).
     *  Feed Audit passes `flow: "FEED_AUDIT"` in the body. */
    trigger: () => 'catalog-operations/trigger',
    /** GET — hybrid progress snapshot (DB checkpoints + live Temporal query):
     *  stage statuses, totals, and the open `pendingGate` when GATED. */
    progress: (workflowId: string) => `catalog-operations/${workflowId}/progress`,
    /** GET (SSE) — same progress DTO pushed on a 5s cadence until terminal. */
    progressStream: (workflowId: string) => `catalog-operations/${workflowId}/progress/stream`,
    /** POST — submit the operator's gate decision (APPROVE/REJECT/RETRY_STAGE/JUMP_BACK).
     *  Body echoes the gate `stage` + a per-action `correlationId`. */
    stageDecision: (workflowId: string) => `catalog-operations/${workflowId}/stage-decision`,
    /** POST — abort a running audit cleanly. Body: `{ reason, requestedBy, correlationId }`. */
    reset: (workflowId: string) => `catalog-operations/${workflowId}/reset`,
    /** POST — restart a failed run from a chosen pipeline stage, reusing the results
     *  of the stages before it. Body: `{ stage, requestedBy, correlationId }`. */
    restartFromStage: (workflowId: string) => `catalog-operations/${workflowId}/restart-from-stage`,
    /** POST — send the operator's APPROVED/REJECTED decision while a run is
     *  paused at AWAITING_APPROVAL. `workflowId` is the same job uuid used as
     *  the `$batchId` route param elsewhere. */
    approvalSignal: (workflowId: string) => `catalog-operations/${workflowId}/signal/approval`,
    /** GET — discover retryable Temporal failure points for a failed batch
     *  workflow. Backed by the generic `workflow-webapp` service (gateway
     *  wildcards `/workflow/**` to it, matching this client's prefix), not the
     *  catalog-operations-specific routes above. */
    failurePoints: (workflowId: string) => `workflows/${workflowId}/failure-points`,
    /** POST — reset the workflow to resume live from a chosen
     *  `WorkflowTaskCompleted` event, reusing all activity results recorded
     *  before it. Body: `{ runId, eventId, reason, reapplySignals }`. */
    retry: (workflowId: string) => `workflows/${workflowId}/retry`,
  },
  /**
   * Scheduled catalog operations — `CatalogOpsScheduleController` on the
   * workflow service, so call these with `catalogOperationsClient` (prefix
   * `/workflow/v1`), the same client as `catalogOperationsWorkflow` above.
   *
   * Schedules are job-first: a schedule points at an existing
   * `enrichment_job.id` uuid, snapshots that job's config, and replays it on
   * every fire. Nothing here carries an organization or subsidiary field —
   * the tenant is resolved server-side from the caller's credentials, and a
   * schedule belonging to another tenant answers 404, never 403.
   *
   * See `docs/catalog-operations-schedules.md`.
   */
  catalogOpsSchedules: {
    /** POST — dry-run a spec before committing: the job's resolved config, its
     *  fingerprint, the duplicate flag, and the upcoming fire times. Drives the
     *  whole create form; `scheduleSpec` is optional (omit it for config +
     *  duplicate check alone). Also the cheapest way to validate a `jobId`. */
    preview: () => 'catalog-operations/schedules/preview',
    /** GET — paged schedule rows (`status`, `connector`, `search`, `health`,
     *  `cursor`, `size`). POST — create from a `jobId`, returns 201 + ACTIVE. */
    root: () => 'catalog-operations/schedules',
    /** GET — aggregate metrics for the ribbon above the list, computed
     *  server-side over the ENTIRE filtered set. Send the same filter params as
     *  `root()`; never derive these by summing loaded rows. */
    ribbon: () => 'catalog-operations/schedules/ribbon',
    /** GET — dashboard rollup (counts, 30-day window, upcoming, needsAttention).
     *  Single windowed query, so it is safe to call regardless of run volume. */
    overview: () => 'catalog-operations/schedules/overview',
    /** GET — cross-schedule run feed (`status`, `triggerType`, `connector`,
     *  `scheduleId`, `since`, `until`, `cursor`, `size`). */
    runs: () => 'catalog-operations/schedules/runs',
    /** GET — aggregate execution tree across every schedule. */
    tree: () => 'catalog-operations/schedules/tree',
    /** GET — schedule detail plus the live Temporal block. PUT — replace
     *  cadence, overlap policy, name and catch-up window. DELETE — soft delete
     *  (pauses, drops the Temporal handle, marks the row `DELETED`; 204). */
    byId: (scheduleId: string) => `catalog-operations/schedules/${scheduleId}`,
    /** PATCH — partial update over the frozen config snapshot. Only `filters`,
     *  `maxProducts`, `batchSize`, `subBatchSize` and `actions` are editable;
     *  a `null` field is left unchanged. */
    config: (scheduleId: string) => `catalog-operations/schedules/${scheduleId}/config`,
    /** POST — stop future fires, keeping the schedule and its history. */
    pause: (scheduleId: string) => `catalog-operations/schedules/${scheduleId}/pause`,
    /** POST — re-enable fires. */
    resume: (scheduleId: string) => `catalog-operations/schedules/${scheduleId}/resume`,
    /** POST — "Run now". Returns 202 + `launcherWorkflowId`, and bypasses the
     *  overlap policy entirely, so it 409s while a run is already in flight. */
    trigger: (scheduleId: string) => `catalog-operations/schedules/${scheduleId}/trigger`,
    /** GET — run history for one schedule, newest-first (`cursor`, `size`). */
    scheduleRuns: (scheduleId: string) => `catalog-operations/schedules/${scheduleId}/runs`,
    /** GET — execution tree for one schedule. */
    scheduleTree: (scheduleId: string) => `catalog-operations/schedules/${scheduleId}/tree`,
  },
  /**
   * Platform ▸ Schedules — read-only, cross-subsidiary view of Catalog Operations schedules
   * (workflow-service `/v1/admin/schedules`, Lily platform admins only; 403 otherwise). Use
   * `catalogOperationsClient` (prefix `/workflow/v1`). Every write stays on the per-subsidiary
   * `catalogOpsSchedules` surface. See `docs/platform-schedules.md`.
   */
  platformSchedules: {
    /** GET — paged, filterable, sortable schedule list. Repeatable `subsidiaryId` / `connector`. */
    list: () => 'admin/schedules',
    /** GET — KPI ribbon + facets over exactly the list's filter set (no sort / paging). */
    summary: () => 'admin/schedules/summary',
    /** GET — recent firings across subsidiaries (paged, sortable). */
    runs: () => 'admin/schedules/runs',
    /** GET — upcoming fire times of ACTIVE schedules, `fireAt` ascending (`limit` ≤ 200). */
    upcoming: () => 'admin/schedules/upcoming',
  },
  /**
   * Failed batch review — internal, cross-subsidiary review of FAILED catalog-ops batches
   * (workflow-service `/v1/admin/batch-review`, super-admin only). Use
   * `catalogOperationsClient` (prefix `/workflow/v1`). The backend ignores the token's
   * subsidiary for these; per-batch calls run under the batch's own subsidiary.
   * See `docs/batch-review.md`.
   */
  batchReview: {
    /** GET — paged failed batches. Query: from, to, subsidiaryId, stage, status, search, page, size. */
    batches: () => 'admin/batch-review/batches',
    /** GET — tile counts, open-by-stage and open-by-subsidiary for the window (ignores status). */
    summary: () => 'admin/batch-review/summary',
    /** GET — one batch + stage errors + first failed products + review events. */
    batch: (jobId: string) => `admin/batch-review/batches/${encodeURIComponent(jobId)}`,
    /** POST — review actions; each returns the updated batch row. */
    delegate: (jobId: string) => `admin/batch-review/batches/${encodeURIComponent(jobId)}/delegate`,
    resolve: (jobId: string) => `admin/batch-review/batches/${encodeURIComponent(jobId)}/resolve`,
    reopen: (jobId: string) => `admin/batch-review/batches/${encodeURIComponent(jobId)}/reopen`,
    notes: (jobId: string) => `admin/batch-review/batches/${encodeURIComponent(jobId)}/notes`,
    /** GET — retryable failure points of the batch's workflow (`?runId=`), under its subsidiary. */
    failurePoints: (jobId: string) =>
      `admin/batch-review/batches/${encodeURIComponent(jobId)}/failure-points`,
    /** POST — retry from a failure point. Body `{ workflowId, eventId, runId?, reason }` → 202. */
    retry: (jobId: string) => `admin/batch-review/batches/${encodeURIComponent(jobId)}/retry`,
    /** GET / PUT — subsidiaries under review (include list). PUT body `{ subsidiaryIds }`. */
    subsidiaries: () => 'admin/batch-review/settings/subsidiaries',
  },
  catalogOperations: {
    /** Aggregated stats for the listing-page cards + alert banner. */
    stats: () => 'jobs/summary',
    /** Paginated batch (job) listing. Filters: status, connectorCode, search, page, size. */
    batches: () => 'jobs',
    /** Per-status job counts for the listing-page tab strip. Shares every
     *  non-status filter with `batches()` — no `status`/`page`/`size` params. */
    statusCounts: () => 'jobs/status-counts',
    /** Full batch detail — the legacy single-call aggregate. Still served for
     *  unmigrated consumers (products page, feed-audit debug, trial). The
     *  batch-detail page uses the four phase endpoints below instead. */
    batchDetail: (jobId: string) => `jobs/${jobId}`,
    /** Phase 1 (shell) — header + config + 5-stage timeline. Fast, DB-light,
     *  no Temporal query, so the page shell paints immediately; read
     *  `workflowId` off it for the `performance-insights/*` calls. Path param
     *  is the job uuid. */
    batchHeader: (jobId: string) => `jobs/${jobId}/header`,
    /** Phase 2 (live progress) — per-stage counters read straight from the
     *  running Temporal workflow, isolated so its latency never blocks the
     *  shell. `204` when the job is terminal/unreachable (fall back to
     *  `header.stages`); poll while the job is running. Path param is the job uuid. */
    batchLiveStage: (jobId: string) => `jobs/${jobId}/live-stage`,
    /** Phase 3 (coverage tabs) — Delivery Health + Enrichment Coverage data
     *  (deliveryHealth, enrichmentCoverage, occasion/tag/trend maps + baselines,
     *  KPIs, gap rollup). Path param is the job UUID.
     *  ⚠️ NOT the same resource as `batchCoverage(workflowId)` below — that one
     *  is the workflow-service Coverage *Signals* cards keyed on the workflow
     *  slug. Same word "coverage", different payload and different id space. */
    batchCoverageDetail: (jobId: string) => `jobs/${jobId}/coverage`,
    /** Phase 4 (heaviest, lazy) — Optimization Quality tab. The single most
     *  expensive section; only fetch it when the operator opens that tab. Path
     *  param is the job uuid. */
    batchOptimizationQuality: (jobId: string) => `jobs/${jobId}/optimization-quality`,
    /** Batch Details Funnel (BE PR #973) — verdict, stages, outcomes, issues,
     *  actions/links and side sections in one read. `{id}` is the job uuid or an
     *  experiment id. Safe to poll while the verdict is RUNNING / WAITING. */
    batchFunnel: (jobId: string) => `jobs/${jobId}/funnel`,
    /** Products behind a funnel number. Query: `stage`, `outcome`, `reason`,
     *  `page` (zero-based), `size`. Answers `source: curation | integrations`
     *  when the list lives upstream (follow `upstreamRef.productsPath`). */
    batchFunnelProducts: (jobId: string) => `jobs/${jobId}/funnel/products`,
    /** One product's journey through the funnel. `productId` is the curation
     *  feed product id from `batchFunnelProducts`. */
    /** Stage errors (BE PR #990) — every recorded stage failure for the batch,
     *  all run attempts, ERRORs first. `{id}` is the job uuid or an experiment id;
     *  no paging. Poll on the funnel's cadence. */
    batchStageErrors: (jobId: string) => `jobs/${jobId}/errors`,
    /** GET — the batch (root run + retry children): reconciled status, counts, runs, actions. */
    batchRuns: (jobId: string) => `jobs/${jobId}/runs`,
    /** GET — products carried over from / moved to other batches + the batch's balance; 404 on older backends. */
    batchCarryover: (jobId: string) => `jobs/${jobId}/carryover`,
    /** GET — what one retry run did to each product it picked. Query: `outcome`, `page`, `size`. */
    batchRunProducts: (jobId: string, runId: string) =>
      `jobs/${jobId}/runs/${encodeURIComponent(runId)}/products`,
    /** GET — every attempt at one product of the batch (root, then each retry). */
    batchProductAttempts: (jobId: string, productId: string) =>
      `jobs/${jobId}/products/${encodeURIComponent(productId)}/attempts`,
    /** GET — Needs Attention, classified (retryable / permanent / not attempted / review). */
    batchNeedsAttention: (jobId: string) => `jobs/${jobId}/needs-attention`,
    /** POST — start a retry child over the batch's remaining products (202). */
    batchRetries: (jobId: string) => `jobs/${jobId}/retries`,
    /** GET — job detail aggregate; carries `postSteps` (SD/PD health) on newer backends. */
    batchPostSteps: (jobId: string) => `jobs/${jobId}`,
    /** GET — post-step (search descriptor / phrase description) retries, newest first;
     *  POST — start one (`dryRun: true` counts only). */
    postStepRetries: (jobId: string) => `jobs/${jobId}/post-step-retries`,
    /** POST — redeliver the products a post-step retry fixed (GMC batches only, 202). */
    postStepRetryRedeliver: (jobId: string, retryId: string) =>
      `jobs/${jobId}/post-step-retries/${encodeURIComponent(retryId)}/redeliver`,
    /** GET — one product's post-step attempts, oldest first. */
    postStepAttempts: (jobId: string, productId: string) =>
      `jobs/${jobId}/products/${encodeURIComponent(productId)}/post-step-attempts`,
    /** POST — accept a step's unhealthy products as they are (`reason` required). */
    postStepAcceptances: (jobId: string) => `jobs/${jobId}/post-step-acceptances`,
    /** POST — undo "accept as is" for some products of a step. */
    postStepAcceptancesUndo: (jobId: string) => `jobs/${jobId}/post-step-acceptances/undo`,
    /** GET — product journey: runs that touched one step. Query: `step`. 404 on older backends. */
    journeyRuns: (jobId: string) => `jobs/${jobId}/journey/runs`,
    /** GET — one run's products in a step. Query: `step`, `class`, `q`, `page`, `size`. */
    journeyRunProducts: (jobId: string, runId: string) =>
      `jobs/${jobId}/journey/runs/${encodeURIComponent(runId)}/products`,
    /** POST — exclude what is still not enriched; DELETE — undo. */
    batchExclusions: (jobId: string) => `jobs/${jobId}/exclusions`,
    batchFunnelProductTrace: (jobId: string, productId: string) =>
      `jobs/${jobId}/funnel/products/${encodeURIComponent(productId)}/trace`,
    /** POST — **Stop**: terminates the job's Temporal workflow and sets
     *  `status = ARCHIVED`. Only accepts a running job; irreversible. Used by the
     *  zero-eligible-products restart guard. Body: `{ reason }`. NOT the
     *  list-visibility archive — that is `setArchived` below. */
    archiveBatch: (jobId: string) => `jobs/${jobId}/archive`,
    /** PATCH — **Archive / Unarchive** (List Archive API): sets or clears
     *  `archivedAt` on a finished job so it leaves / rejoins the default list.
     *  Touches no workflow and no status; exactly reversible. Body:
     *  `{ archived: boolean }`. AWAITING_APPROVAL rows are archivable; the
     *  server probes Temporal and 409s with `{ error: { code: JOB_STILL_LIVE |
     *  JOB_DELIVERING | ARCHIVE_STATE_CHANGED, fields: { liveness, workflowId } } }`
     *  only when something is actually executing. Response is the full list row
     *  incl. `archivedBy` / `lastActionBy`. Shared by Catalog Operations and
     *  Feed Audit (same `enrichment_job` row). */
    setArchived: (jobId: string) => `jobs/${jobId}/archived`,
    /** POST — bulk archive / unarchive. Body `{ ids: string[] (1–100), archived }`.
     *  Always 200 when well-formed; read `summary` + `results[].outcome`. The
     *  literal `:` is part of the path. */
    setArchivedBatch: () => 'jobs/archived:batch',
    /** POST — non-destructive repair for a row stuck at IN_PROGRESS whose run
     *  actually finished: probes Temporal and stamps FAILED (never COMPLETED)
     *  when the workflow is closed. `reconciled: false` is a success — read
     *  `detail`. The replacement for reaching for Stop on a dead run. */
    reconcile: (jobId: string) => `jobs/${jobId}/reconcile`,
    /** Products belonging to a batch. NOT YET WIRED — placeholder for next PR. */
    batchProducts: (jobId: string) => `jobs/${jobId}/products`,
    /** GET — paginated enriched products from a completed pipeline run (`?page`&`size`).
     *  Accepts the job UUID or the workflow slug. Powers the Feed Audit preview step. */
    pipelineEnrichedProducts: (jobId: string) => `${jobId}/pipeline/enriched-products`,
    /** Single product detail. NOT YET WIRED — placeholder for next PR. */
    productDetail: (jobId: string, productId: string) => `jobs/${jobId}/products/${productId}`,
    /** Per-product enrichment payload (scores + field-level optimizations).
     *  Path param is the curation feed_product_id. Query param `includeFailed`
     *  defaults to false. */
    productEnrichment: (productId: string) => `products/${productId}`,
    /** GET — every enrichment run for one catalog product, newest first (max 50).
     *  Keyed by the catalog `externalId` (query param — GMC ids contain `/` and `~`),
     *  not the feed product id; optional `connectorCode` (GMC | META). */
    productEnrichmentHistory: () => 'products/enrichment-history',
    /** On-demand data-scoring for one product — score / re-score outside the
     *  batch sample cap. `POST` scores (optional `{ overrideCache }` body;
     *  optional `connectorCode` query); `GET` reads back the persisted PRE/POST
     *  (poll). `workflowId` is the enrichment/pipeline/job id already in scope
     *  (accepts the pipeline, sample, or job id); `productId` is the curation
     *  feed_product_id. Resolves to `/workflow/v1/enrichments/{workflowId}/
     *  products/{productId}/score` via `catalogOpsClient`. See
     *  `docs/on-demand-product-scoring.md`. */
    productScore: (workflowId: string, productId: string) =>
      `${workflowId}/products/${productId}/score`,
    /** GET — which locales a run has been translated into, one entry per (locale, phase) with
     *  PASS/FLAG/FAIL counts; `[]` when never translated. POST — translate the run's enriched
     *  English into more locales (`{ locales, productIds? }` → 202, rows land in the
     *  background; poll the GET). `workflowId` may be the job, pipeline or sample id. See
     *  workflow-service `docs/api/localized-content-ui-contract.md`. */
    translations: (workflowId: string) => `${workflowId}/translations`,
    /** GET — one product's content in every stored locale, with QA verdict and `stale`. */
    productTranslations: (workflowId: string, productId: string) =>
      `${workflowId}/products/${productId}/translations`,
    /** Operator edit to one enriched attribute on a product. Path param is the
     *  curation feed_product_id; `field` is the optimization's wire field name,
     *  percent-encoded. Query param `connectorCode` (default `GMC`) selects the
     *  enrichment table. The contract is frozen — the UI shipped against it
     *  first. */
    productAttribute: (productId: string, field: string) =>
      `products/${productId}/attributes/${encodeURIComponent(field)}`,
    /** Provenance for every operator override recorded against a product,
     *  newest-first. Query param `connectorCode` (default `GMC`).
     *
     *  This is *who and when*, not *what*: the value a product actually carries
     *  lives on the enrichment row and comes back from `productEnrichment`. Never
     *  404s — an unknown product and one with no edits both answer `[]`. */
    productAttributeEdits: (productId: string) => `products/${productId}/attribute-edits`,
    /** Content-delta gap-analysis payload for a single product (`gap_analysis_v3`):
     *  every baseline and enriched attribute, each enriched one flagged net-new or
     *  a restatement. Path param is the curation feed_product_id — same id as
     *  `productEnrichment`. Query params `includeFailed` (default false) and
     *  `connectorCode` (default `GMC`). Served from persisted tables, so it is a
     *  normal-latency read despite the data being LLM-derived upstream. */
    productGapAnalysis: (productId: string) => `products/${productId}/gap-analysis`,
    /** Forces a recompute of one product's gap analysis: invalidates the cached
     *  result, marks it PENDING and re-runs the agent off-thread. Returns 202 with
     *  a PENDING envelope, after which the caller polls `productGapAnalysis`. */
    productGapAnalysisRefresh: (productId: string) => `products/${productId}/gap-analysis/refresh`,
    /** Search descriptors *and* phrase descriptions for one product — the two POST
     *  enrichment strategies share a single read. Path param is the curation
     *  feed_product_id, same id as `productEnrichment`. A plain DB read (no agent
     *  call, no history surface), so it needs no polling or refresh.
     *
     *  Optional query param `enrichmentJobId` pins the read to one run. The
     *  descriptor tables are producer-keyed (`experiment_id` XOR
     *  `enrichment_job_id`), so a product enriched by more than one job has more
     *  than one set: without the param the backend derives the producer from the
     *  product's *latest* successful enrichment, which is not necessarily the
     *  batch being viewed. Always send it from a batch-scoped page.
     *
     *  404 = no enrichment context, or neither strategy produced for the
     *  resolved producer. */
    productSearchDescriptors: (productId: string) => `products/${productId}/search-descriptors`,
    /** Search Impact tab (descriptor coverage + change ledger + review), computed by
     *  agentic-integrations and served from workflow-service's product_search_impact
     *  tables. Pin the producer with `enrichmentJobId` (or `experimentId`), same as
     *  `productSearchDescriptors`. `status: NOT_COMPUTED` = no row yet (offer refresh). */
    productSearchImpact: (productId: string) => `products/${productId}/search-impact`,
    /** POST — recompute one product synchronously (deterministic) and return the fresh body. */
    productSearchImpactRefresh: (productId: string) =>
      `products/${productId}/search-impact/refresh`,
    /** POST (body `{ reason? }`) dismisses a review item; DELETE undoes it. Keyed on the
     *  item's stable `itemKey`, so a dismissal survives recomputes. */
    productSearchImpactDismiss: (productId: string, itemKey: string) =>
      `products/${productId}/search-impact/review/${encodeURIComponent(itemKey)}/dismiss`,
    /** GET — batch strip: share of addressable search volume covered, before → after. */
    jobSearchImpactSummary: (jobId: string) => `jobs/${jobId}/search-impact/summary`,
    /** Performance Signals tab data — popular terms/attributes and
     *  occasions/trends coverage, each as ranked before/after rows. Path param
     *  is the workflow id (`BatchDetail.batchId`), not the job uuid — same
     *  identifier `approvalSignal` above takes. */
    batchInsights: (workflowId: string) => `${workflowId}/insights`,
    /** Drill-down behind any Product Terms / Occasion / Popular Attribute /
     *  Macro or Micro Trend row — the products behind one label, paginated.
     *  Same client/base as `batchInsights` (same workflow id path param), a
     *  sibling read over the same insights data, not the unrelated
     *  `performanceInsights` group below (that's a different backend surface
     *  for the AI-Performance feature). `category`/`label`/`productCategory`/
     *  `group`/`page`/`size` go as Ky `searchParams` from the service layer. */
    insightMembershipProducts: (workflowId: string) =>
      `${workflowId}/performance-insights/membership/products`,
    /** Enrichment Coverage tab signal cards — coverage by category/attribute,
     *  attribute depth, and the four product-detail change states, each as
     *  ranked rows. Same workflow-service client and same path param as
     *  `batchInsights` above (the workflow id, not the job uuid); the two are
     *  siblings over one table with disjoint categories.
     *
     *  No `periodType` param: coverage is only ever written as MONTHLY, so
     *  scoping by its own categories is already exact.
     *
     *  Returns 200 with empty arrays for an unknown run, a wrong tenant, and a
     *  run whose async coverage callback hasn't landed — the three are not
     *  distinguishable, so callers poll. */
    batchCoverage: (workflowId: string) => `${workflowId}/coverage`,
  },
  /**
   * Feed Audit exports — server-generated downloadable files. Use `exportClient`
   * (prefix `/api/v1/export`), NOT `catalogOperationsClient`. Responses are file
   * bytes (read as a Blob); the filename rides `Content-Disposition` and
   * truncation/degradation flags ride `X-Feed-Audit-Truncated` / `X-Feed-Audit-Degraded`.
   */
  /** Meta feed audit — on `catalogOperationsClient` (`/workflow/v1`). */
  feedAuditMeta: {
    /** GET — the Meta rules (closed-value lists, scorer scope, title + image rules)
     *  the Meta audit applies to raw values. Static per agent deploy; cache on `version`. */
    spec: () => 'feed-audit/meta/spec',
  },
  feedAuditExport: {
    /** GET — CSV/TSV analytics workbook. Query: `format` (`csv` default | `tsv`) and
     *  `workflowId` (parent run slug — keys the change-summary section). */
    csv: (feedAuditId: string) => `feed-audit/${feedAuditId}/export`,
    /** GET — self-contained HTML before/after report. Query: `workflowId`. */
    report: (feedAuditId: string) => `feed-audit/${feedAuditId}/report`,
  },
  /**
   * Insight-membership drawer exports — the full product list behind one Product
   * Terms / Occasion / Popular Attribute / Trend row, built by export-service from
   * workflow-service's membership export read. Use `exportClient` (prefix
   * `/api/v1/export`). Query: `category`, `label`, `group` (omitted for All),
   * `productCategory`, `format`. Filename rides `Content-Disposition`, the row
   * count rides `X-Export-Row-Count`; over the row cap the server answers 413.
   */
  insightMembershipExport: {
    products: (workflowId: string) =>
      `performance-insights/${encodeURIComponent(workflowId)}/membership/products`,
  },
  /**
   * AI Performance Insights — the precomputed panels behind the AI-Performance
   * dashboard, hung off the enrichment run they describe:
   * `/workflow/v1/enrichments/{workflowId}/performance-insights/**`.
   *
   * Call these with `catalogOpsClient` (prefix `/workflow/v1/enrichments`), so
   * the paths below start at the run id — the same run-scoped shape as the
   * sibling reads (`enrichments/${wfId}/pipeline/quality-score`,
   * `enrichments/${wfId}/sample-enrichment/preview`).
   *
   * Nesting under an EXISTING resource is deliberate: it needs no new Vite dev
   * proxy alias and no new Firebase Hosting rewrite. Every rewrite here is
   * path-scoped, so a new top-level resource would fall through to the SPA
   * catch-all `**` and return `index.html` with a 200 instead of JSON.
   *
   * `workflowId` is the enrichment run slug (e.g. `bombas-ak-catalog-ops-1`) and
   * is encoded because run slugs can carry timestamps/colons. Tenant comes from
   * the `X-Subsidiary-ID` header the subsidiary interceptor injects — never a
   * query param.
   */
  performanceInsights: {
    /** GET — screens 1/2/4 + the freshness banner in one payload, bucketed by
     *  SCREAMING_SNAKE category (`READINESS_DIMENSION`, `FUNNEL_STAGE`,
     *  `FUNNEL_QUERY_TYPE`, `ATTRIBUTE_COMPLETENESS`, `AI_PERFORMANCE_RUN`).
     *  Every bucket is always present. A run with no panel rows is a 200 with
     *  empty arrays, never a 404 — so empty is ambiguous between "not computed
     *  yet", "unknown run" and "wrong tenant", and only the presence of an
     *  `AI_PERFORMANCE_RUN` row separates the first from the other two. */
    summary: (workflowId: string) =>
      `${encodeURIComponent(workflowId)}/performance-insights/summary`,
    /** GET — screen 3's demand-gap terms bucketed by quadrant (`FIX_NOW` /
     *  `DEFEND` / `NICHE` / `MONITOR`). Deliberately unpaginated and not
     *  filterable by quadrant: the quadrant IS the envelope key (the shared row
     *  DTO carries no category), and the loader caps each bucket at 50 rows.
     *  Split off `summary` so the term list never blocks the first paint. */
    demandGap: (workflowId: string) =>
      `${encodeURIComponent(workflowId)}/performance-insights/demand-gap`,
    /** GET — screen 5's live per-product drill-down (not precomputed). Optional
     *  `connectorCode` query param, passed as Ky `searchParams` from the service
     *  layer, takes the enum NAME and defaults to `GMC`; any other value is a
     *  400 rather than an empty 200. 404 = unknown run, or no enrichment row for
     *  the product in the caller's tenant. */
    product: (workflowId: string, productId: string) =>
      `${encodeURIComponent(workflowId)}/performance-insights/products/${encodeURIComponent(productId)}`,
    /** GET — recompute state for the AI-Performance panels, made refresh-safe:
     *  a page refresh mid-recompute re-reads `RECOMPUTING` from the server
     *  rather than losing it with the tab. Always `200` (never `404`) —
     *  `NONE` means "never computed". Takes the workflow slug, not the job uuid. */
    status: (workflowId: string) => `${encodeURIComponent(workflowId)}/performance-insights/status`,
  },
  /**
   * Curation service — backs the per-batch product listing reached from the
   * catalog-operations batch detail page. Use `curationClient`, not
   * `catalogOpsClient`, since the URL prefix differs (`/curation` vs
   * `/workflow/v1/enrichments`).
   */
  curation: {
    /** Paginated products for a curation run. Path param is the curationRefId
     *  (uuid) carried over from the jobs listing payload. Query params:
     *  page (1-based), size. */
    runProducts: (curationRefId: string) => `runs/${curationRefId}/products`,
    /** Single product detail. Path param is the curation feed_product_id
     *  (uuid) — the same id used as `Product.id` in the listing payload. */
    productDetail: (productId: string) => `products/${productId}`,
    /**
     * Version timeline for a FEED product, newest first: the live row then each
     * archived version, with the runs that wrote and retired it and
     * `changed_fields` against the NEXT OLDER version (not against the live row).
     *
     * `products/{id}` is the feed product — the raw connector row — despite the
     * name. The curated product's own timeline is `internalProductVersions`
     * below; the two are different entities with independent counters.
     */
    productVersions: (feedProductId: string) => `products/${feedProductId}/versions`,
    /** One version of a feed product in full, live or archived. Pass a run's
     *  `feed_product_version` to render exactly what that run processed. */
    productVersion: (feedProductId: string, version: number) =>
      `products/${feedProductId}/versions/${String(version)}`,
    /** Version timeline for an INTERNAL (curated) product, payload inline. Moves
     *  independently of the feed timeline: a feed-only change (price, say)
     *  versions the feed row and leaves this one alone. */
    internalProductVersions: (productId: string) => `internal-products/${productId}/versions`,
    /** POST — batch product query for a curation run. Body: `{ product_ids }`.
     *  Returns all requested products' current values in one call (avoids one
     *  detail request per product). Path param is the curationRefId (uuid). */
    runProductsQuery: (curationRefId: string) => `runs/${curationRefId}/products/query`,
    /** Cross-batch needs-attention products listing. Required query params:
     *  subsidiary_id, connector_code; optional page (1-based), size. */
    needsAttention: () => 'needs-attention',
    /** Subsidiary-scoped catalog aggregates (total / valid / needs_attention)
     *  with per-connector breakdown. Subsidiary is read from the
     *  `x-subsidiary-id` header injected by the curation client. */
    stats: () => 'stats',
    /** Filter definitions (with option lists pre-populated from the
     *  subsidiary's data). Optional query param `connector_code` narrows the
     *  list to connector-specific filters; omitted = overlap set. */
    filters: () => 'filters',
    /** Filter definitions discovered from ONE run's products (fields that carry a
     *  value in the run, with options / bounds from those products). Required
     *  query param `connector_code` must match the run's connector. Keys are
     *  `attr.*` / `extra.*` and are accepted by `runProducts`. */
    runFilters: (curationRefId: string) => `runs/${curationRefId}/filters`,
    /** POST — pre-flight already-curated breakdown for an EXPLICIT source-id
     *  list (① in the contract). Body: `AlreadyCuratedQueryRequest`. Subsidiary
     *  is read from the `X-Subsidiary-ID` header (auto-injected). Reserved: no
     *  explicit row-level product selection surface exists in the app today. */
    alreadyCurated: () => 'already-curated',
    /** POST — run-scoped already-curated reconciliation (② in the contract).
     *  Path param is the curationRefId (uuid); connector + subsidiary derive
     *  from the run. An empty body self-serves the run's own selection. Returns
     *  `400` for whole-sync runs (no `request_params.filters.product_ids`), so
     *  callers must handle that. Gate the UI on `meta.skipped_semantics_valid`. */
    runAlreadyCurated: (curationRefId: string) =>
      `runs/${encodeURIComponent(curationRefId)}/already-curated`,
    /** POST — submit an async whole-selection breakdown job (③ in the contract)
     *  for a `syncId` + `filters` scope (no explicit id list). Body:
     *  `BySelectionSubmitRequest`. Returns `202 { job_id, status }`; poll via
     *  `alreadyCuratedBySelectionStatus`. */
    alreadyCuratedBySelection: () => 'already-curated/by-selection',
    /** GET — poll a by-selection breakdown job (③). Path param is the `job_id`
     *  from the submit call. Tenant-scoped: another subsidiary's job → 404. */
    alreadyCuratedBySelectionStatus: (jobId: string) =>
      `already-curated/by-selection/${encodeURIComponent(jobId)}`,
    /** POST — row drill-down (④ in the contract). Lists the product ROWS for one
     *  bucket (`clean` | `flagged`) of a selection given as explicit ids (SUBSET)
     *  or `sync_id`+`filters` (WHOLE). Keyset-paginated (`cursor`), rows only —
     *  counts stay on ①/③. Run-independent → valid pre- and post-run. */
    alreadyCuratedBySelectionRows: () => 'already-curated/by-selection/rows',
  },
  /**
   * Service Auth — OAuth2 client_credentials principals managed via identity-service.
   * Backs the /platform/service-auth/** UI: service-user CRUD, credential issue/rotate/revoke,
   * kill-switch, and OIDC discovery / JWKS for token verifiers.
   */
  serviceAuth: {
    users: () => 'admin/platform/service-users',
    userById: (id: string) => `admin/platform/service-users/${id}`,
    killUser: (id: string) => `admin/platform/service-users/${id}/kill`,
    issueCredential: (id: string) => `admin/platform/service-users/${id}/credentials`,
    rotateCredential: (id: string, credId: string) =>
      `admin/platform/service-users/${id}/credentials/${credId}/rotate`,
    revokeCredential: (id: string, credId: string) =>
      `admin/platform/service-users/${id}/credentials/${credId}/revoke`,
    jwks: () => '.well-known/jwks.json',
    oidcDiscovery: () => '.well-known/openid-configuration',
    /** Code-driven audience registry — used to populate the principal allowlist picker. */
    audiences: () => 'oauth/audiences',
    /** Code-driven scope registry — used to populate the principal allowlist picker. */
    scopes: () => 'oauth/scopes',
  },
  /** Audit Logs — system-admin endpoints. Backend exposes POST /admin/audit-logs/search
   *  with a nested body { filters, pagination } and GET /admin/audit-logs/:id. */
  auditLogs: {
    search: () => 'admin/audit-logs/search',
    byId: (id: string) => `admin/audit-logs/${id}`,
  },
  notifications: {
    /** Channel config (admin) — list / get / upsert / test. */
    channelConfigs: () => 'admin/channel-config',
    channelConfigByType: (channelType: string) => `admin/channel-config/${channelType}`,
    channelConfigTest: (channelType: string) => `admin/channel-config/${channelType}/test`,
    /** Notification configurations (alert topics). */
    configurations: () => 'admin/configurations',
    configurationById: (id: string) => `admin/configurations/${id}`,
    /** User preferences per (configuration × channel). */
    preferences: () => 'preferences',
    /** Bulk-upsert one channel target+status across many configuration IDs in one request. */
    preferencesBulk: () => 'preferences/bulk',
    preferencesSeed: () => 'preferences/seed',
    preferenceById: (id: string) => `preferences/${id}`,
    preferenceHistory: (id: string) => `preferences/${id}/history`,
    preferenceRestore: (id: string, historyId: number | string) =>
      `preferences/${id}/restore/${String(historyId)}`,
    /** Server-Sent Events stream for live in-app notifications (subsidiary-scoped). */
    stream: () => 'notifications/stream',
    /** POST /notifications/inbox/list — paginated inbox for the current user. */
    inbox: () => 'notifications/inbox/list',
    /** GET /notifications/inbox/unread-count — live unread count for the bell badge. */
    inboxUnreadCount: () => 'notifications/inbox/unread-count',
    /** POST /notifications/read-all — bulk mark all unread as read. */
    inboxMarkAllRead: () => 'notifications/read-all',
    /** POST /notifications/{id}/read — mark a single notification as read. */
    markNotificationRead: (id: string) => `notifications/${id}/read`,
    /** POST /notifications/{id}/dismiss — dismiss a notification from the feed. */
    dismissNotification: (id: string) => `notifications/${id}/dismiss`,
    /** POST /notifications/list — full notifications list (all, paginated). */
    allNotifications: () => 'notifications/list',
  },
  contentEnrichment: {
    /** Aggregated catalog quality + eligibility summary keyed by connection id. */
    summary: (connectionId: string) => `products/${connectionId}/summary`,
    sampleGenerate: () => 'content-enrichment/sample/generate',
    sampleResults: () => 'content-enrichment/sample/results',
    fullGenerate: () => 'content-enrichment/full/generate',
    fullStatus: (jobId: string) => `content-enrichment/full/${jobId}/status`,
    comparison: () => 'content-enrichment/comparison',
  },
  /**
   * Feature Flags — identity-service admin endpoints (`apiClient`). The
   * `me` endpoint returns the resolved flag map for the current user and
   * is intentionally unprefixed by `admin/`.
   */
  /**
   * Coverage Report — cross-subsidiary enrichment coverage aggregates.
   * Issued via `integrationsClient`, so these ride the `/api/v1/integration/**`
   * rewrite the gateway maps to `/api/v1/integration-service/coverage/report*`.
   * Not scoped by `X-Subsidiary-ID`; visibility is gated by `CONNECTIONS:READ` plus a Lily admin.
   * All four take `subsidiaryIds` (repeated keys) and `groupBy` (`subsidiary` | `connector`).
   */
  coverageReport: {
    /** GET — paginated cross-subsidiary rows. Params: tab, connectorCode, deliveryMechanism, coverageType, search, subsidiaryIds, groupBy, reason, sortBy, sortDesc, page, size. */
    report: () => 'coverage/report',
    /** GET — aggregate header stats and picker facets for the current filter set. */
    reportStats: () => 'coverage/report/stats',
    /** GET — where coverage is lost across the filtered rows, one item per reason code. */
    reportInsights: () => 'coverage/report/insights',
  },
  /**
   * Coverage settings — the subsidiary-wide "Coverage tracking" on/off switch
   * (integration-service, coverage v3 spec §9). Unlike `coverageReport` this IS scoped
   * by `X-Subsidiary-ID`: `CONNECTIONS:READ` to read, `CONNECTIONS:UPDATE` to change.
   */
  coverage: {
    /** GET/PUT — `{ enabled, connectors }` for the caller's subsidiary. GET answers
     *  `enabled=false` + both connectors when no row exists yet. */
    settings: () => 'coverage/settings',
  },
  featureFlags: {
    me: () => 'feature-flags/me',
    search: () => 'admin/feature-flags/search',
    create: () => 'admin/feature-flags',
    byId: (id: string) => `admin/feature-flags/${id}`,
    toggle: (id: string) => `admin/feature-flags/${id}/toggle`,
    evaluate: (id: string) => `admin/feature-flags/${id}/evaluate`,
    override: (id: string, subId: string) => `admin/feature-flags/${id}/overrides/${subId}`,
    /** Export/import (move Feature Flag definitions between environments) — SuperAdminGuard / SystemAdminGuard. */
    exportPreview: () => 'admin/feature-flags/export/preview',
    export: () => 'admin/feature-flags/export',
    importPreview: () => 'admin/feature-flags/import/preview',
    import: () => 'admin/feature-flags/import',
  },
  adminCatalog: {
    /** GET — full platform product catalog for admin management. */
    products: () => 'admin/catalog/products',
    /** PATCH — replace a product's mutable fields. */
    updateProduct: (code: string) => `admin/catalog/products/${code}`,
  },
  /**
   * Enriched-product export lifecycle — user-initiated download of a completed
   * enrichment run. Served by **agentic-export-service** (`exportClient`), the
   * same service as `exportTemplates` below, so the trigger reaches the code
   * that implements the feed encodings and `template` is honoured.
   *
   * Moved as one block, never piecemeal: the export row is created by whichever
   * service handles the trigger, so a trigger here with a status read on
   * workflow-webapp would look up an `exportId` that service has never seen.
   *
   * Three backend fixes had to land first, and this switch was reverted twice
   * before they all had:
   *   1. iac-modules #684 — `export_user` held SELECT-only on the `workflow`
   *      schema, so the trigger's INSERT was denied and answered a bare 500.
   *   2. iac-modules #685 — `TEMPORAL_TARGET` was unset, so it dialled
   *      localhost:7233.
   *   3. agentic-export-service #40 — the export now runs in-process in the API
   *      (`export_execution_mode = "inprocess"`), so no Temporal worker
   *      deployment is required for it to execute.
   *
   * Same doubled `v1` as `exportTemplates` — see the note there.
   *
   * The SSE status stream
   * has its own base URL (`exportsSseBaseUrl` in `~/constants/config/env.ts`) so
   * it can bypass the Firebase Hosting CDN buffer — see `export-status-stream.ts`.
   */
  exports: {
    /** POST — dry-run product count + size estimate (no workflow started). */
    estimate: () => 'v1/exports/estimate',
    /** POST — start an export. Returns exportId (the primary FE handle). */
    trigger: () => 'v1/exports/trigger',
    /** GET — poll a single export's status (mints a fresh downloadUrl on COMPLETED). */
    status: (exportId: string) => `v1/exports/${exportId}`,
    /** GET (SSE) — stream the status DTO every 2s; auto-closes on terminal. */
    statusStream: (exportId: string) => `v1/exports/${exportId}/status/stream`,
    /** GET — subsidiary's exports, newest first (filter by sourceWorkflowId/status). */
    list: () => 'v1/exports',
    /** POST — signal a running export to cancel. 204; 409 if already terminal. */
    cancel: (exportId: string) => `v1/exports/${exportId}/cancel`,
    /** POST — mint a fresh 48h signed download URL for a COMPLETED export. */
    refreshDownloadUrl: (exportId: string) => `v1/exports/${exportId}/downloadUrl:refresh`,
    /** GET — the configuration the export ran with (captured at trigger); 404 if none recorded. */
    config: (exportId: string) => `v1/exports/${exportId}/config`,
    /** DELETE — remove a terminal export's DB row + GCS blob. 409 if non-terminal. */
    delete: (exportId: string) => `v1/exports/${exportId}`,
    /** POST — hide one export from the main list (`aes-delivery`, in progress). Idempotent. */
    archive: (exportId: string) => `v1/exports/${exportId}/archive`,
    /** POST — unarchive one export. Idempotent. */
    restore: (exportId: string) => `v1/exports/${exportId}/restore`,
    /** POST — bulk archive, body `{ ids: string[] }` (≤500 per the service's own doc). */
    bulkArchive: () => 'v1/exports/archive',
    /** POST — bulk restore, same body shape as `bulkArchive`. */
    bulkRestore: () => 'v1/exports/restore',
    /** GET — `{byStatus, expired, archived, createdSince, oldestReadyAt}` (in progress on
     *  `aes-delivery`) — exact KPI/tab counts for the Catalog exports list, scoped by the same
     *  `archived`/`q` filters as `list()`. 404 until deployed — callers fall back to a windowed
     *  client computation over the loaded rows when this errors. */
    counts: () => 'v1/exports/counts',
  },
  /**
   * Full-catalog export — every product a subsidiary enriched, across ALL its
   * batches, one version per SKU (export-service `docs/catalog-export-api.md`).
   * Same `exportClient` and doubled `v1` as `exports`; a catalog export is an
   * ordinary export row, so status, SSE, history and download reuse `exports.*`
   * (history: `sourceWorkflowId=catalog:GMC` / `catalog:META`).
   */
  catalogExports: {
    /** GET — the batch picker (`connector`, `preset`, `includeArchived`); cached 60 s server-side. */
    batches: () => 'v1/exports/catalog/batches',
    /** POST — the metrics bar (SKUs, products, duplicates collapsed, markets…); cached 5 min. */
    summary: () => 'v1/exports/catalog/summary',
    /** POST — dry-run row count + measured file size, like the batch estimate. */
    estimate: () => 'v1/exports/catalog/estimate',
    /** POST — first SKUs rendered raw + encoded, each with where it came from (`rows[].meta`). */
    preview: () => 'v1/exports/catalog/preview',
    /** POST — keyset pages of the catalog by SKU or by product; `search` ≥ 3 chars. */
    browse: () => 'v1/exports/catalog/browse',
    /** POST — every version of some SKUs / products, with the one that ships marked. */
    versions: () => 'v1/exports/catalog/versions',
    /** POST — distinct shipped values (with counts) of some columns, for dropdown filters. */
    facets: () => 'v1/exports/catalog/facets',
    /** POST — the rows the file ships, grouped by batch (product + external ids, ≤ 50 000):
     *  what "Validate my export" sends to enrichment validation, one SCOPED run per batch. */
    validationTargets: () => 'v1/exports/catalog/validation-targets',
    /** POST — start a catalog export → 202; 409 when an identical one is running. */
    trigger: () => 'v1/exports/catalog/trigger',
  },
  /**
   * File Transformation Engine — transformer library, versions, validate, preview,
   * step registry and the resolver. Served by **agentic-integrations**, which owns
   * all transformer config and runs (export-service is only its stateless engine,
   * never called by the webapp for transformer work). Issued via
   * `integrationsClient`, so `transformers/*` rides the `/api/v1/integration/**`
   * rewrite the gateway maps to `/api/v1/integration-service/transformers/*` —
   * no doubled `v1` (that was export-service's dual mount). Writes are super
   * admins only (enforced server-side); `resolve` is open to any subsidiary user.
   */
  transformers: {
    /** GET — library (`connector`, `format`, `q`, `includeArchived`); POST — create. */
    list: () => 'transformers',
    /** GET — one transformer with its definition; PATCH — metadata; DELETE — archive (409 IN_USE). */
    detail: (id: string) => `transformers/${encodeURIComponent(id)}`,
    /** POST — save a new version `{ definition, note }`. */
    versions: (id: string) => `transformers/${encodeURIComponent(id)}/versions`,
    /** GET — one saved version's definition. */
    version: (id: string, version: number) =>
      `transformers/${encodeURIComponent(id)}/versions/${String(version)}`,
    /** POST — validate a definition (integrations proxies export-service inspect). */
    validate: () => 'transformers/validate',
    /** POST — run a draft definition on 10–50 real products (proxied to the engine). */
    preview: () => 'transformers/preview',
    /** GET — palette + settings schemas for content steps, writers and file steps. */
    stepTypes: () => 'transformers/step-types',
    /** GET — which transformer(s) apply to a subsidiary/connector/connection (≥1 item). */
    resolve: () => 'transformers/resolve',
  },
  /** Transformer assignment records — agentic-integrations, `integrationsClient`. */
  transformerAssignments: {
    /** GET — list (`subsidiaryId`, `transformerId`, `connectionId`); POST — create. */
    list: () => 'transformer-assignments',
    /** PATCH — change transformer / pinned version; DELETE — remove. */
    detail: (id: string) => `transformer-assignments/${encodeURIComponent(id)}`,
  },
  /**
   * Export profiles — agentic-integrations (`integrationsClient`), in progress at
   * `int-delivery` (uncommitted; `apps/api/src/app/api/v1/export_profiles.py`). A repeatable,
   * scheduled catalog delivery: which rows, which file layout, when, the checks it must pass
   * before it's ready, and where the file goes. Distinct from `src/features/deliveries/` (the
   * existing integration-service pipeline-run console) — unrelated domains that happen to share
   * the English word "delivery"; this one is catalog-export scheduling only.
   */
  exportProfiles: {
    /** GET — list (`status`, `connector`, `page`, `size`); POST — create. */
    list: () => 'export-profiles',
    /** GET — one profile (includes `nextRunAt`, unlike the list rows); PATCH (needs `version`); DELETE. */
    detail: (id: string) => `export-profiles/${encodeURIComponent(id)}`,
    /** POST — pause an ACTIVE profile (new runs no longer schedule; Run now stays available only
     *  via resume first — the service rejects a trigger on a PAUSED profile). */
    pause: (id: string) => `export-profiles/${encodeURIComponent(id)}/pause`,
    /** POST — resume a PAUSED profile. */
    resume: (id: string) => `export-profiles/${encodeURIComponent(id)}/resume`,
    /** POST — Run now (202, trigger=MANUAL); 409 if the profile is PAUSED. */
    triggerRun: (id: string) => `export-profiles/${encodeURIComponent(id)}/runs`,
    /** GET — this profile's own runs, newest first (page/size only — no status/date filter here;
     *  use `exportProfileRuns.list` for that). */
    runs: (id: string) => `export-profiles/${encodeURIComponent(id)}/runs`,
    /** POST — dry run: rows/dropped/net-new/file name, no export created. Body `{ previewRows? }`. */
    dryRun: (id: string) => `export-profiles/${encodeURIComponent(id)}/dry-run`,
  },
  /** Export profile runs — cross-profile list + per-run actions. Sibling router (not nested
   *  under `export-profiles/{id}`) because `profileId` is nullable (ON DELETE SET NULL). */
  exportProfileRuns: {
    /** GET — `status`, `profileId`, `archived` (default false), `createdFrom`, `createdTo`,
     *  keyset `cursor`/`limit` (not page/size — confirmed against int-delivery's own schema). */
    list: () => 'export-profile-runs',
    /** GET — single run; uniquely also populates `files[]` (fresh signed URLs). */
    detail: (id: string) => `export-profile-runs/${encodeURIComponent(id)}`,
    /** POST — approve a BLOCKED run anyway. Body `{ reason }` (required, 1–1000 chars). */
    approve: (id: string) => `export-profile-runs/${encodeURIComponent(id)}/approve`,
    /** POST — mark a READY (or DELIVERY_FAILED) run delivered. Body `{ deliveredTo, note? }`. */
    markDelivered: (id: string) => `export-profile-runs/${encodeURIComponent(id)}/mark-delivered`,
    /** POST — SFTP profiles: push a READY run now / retry a DELIVERY_FAILED push. 202; the run
     *  comes back DELIVERING. 409 from any other status or when SFTP isn't ready. */
    deliver: (id: string) => `export-profile-runs/${encodeURIComponent(id)}/deliver`,
    /** POST — cancel a run still in QUEUED/REFRESHING/BUILDING/CHECKING. */
    cancel: (id: string) => `export-profile-runs/${encodeURIComponent(id)}/cancel`,
    /** POST — archive / restore one run. */
    archive: (id: string) => `export-profile-runs/${encodeURIComponent(id)}/archive`,
    restore: (id: string) => `export-profile-runs/${encodeURIComponent(id)}/restore`,
    /** POST — bulk archive/restore, body `{ run_ids: string[] }` (≤200). */
    bulkArchive: () => 'export-profile-runs/archive',
    bulkRestore: () => 'export-profile-runs/restore',
    /** GET — `{byStatus, archived, deliveredRowsSince, deliveredSince, oldestReadyAt}` (in
     *  progress on `int-delivery`) — exact KPI/tab counts for the Catalog exports list, scoped
     *  by the same `archived` filter as `list()`. 404 until deployed — callers fall back to a
     *  windowed client computation over the loaded rows when this errors. */
    counts: () => 'export-profile-runs/counts',
  },
  /**
   * Export schedules — agentic-integrations (`integrationsClient`). A schedule is only WHEN an
   * export profile runs (calendar / every N hours / once); everything about WHAT ships stays on
   * the profile. A profile has 0..n schedules; each firing either starts a profile run or is
   * recorded as skipped (profile paused, run already in flight, setup incomplete).
   */
  exportSchedules: {
    /** GET — one profile's schedules (oldest first); POST — create one (201). 422
     *  `SCHEDULE_REQUIRES_DESTINATION` / `ROWS_REQUIRED` when the profile isn't ready. */
    byProfile: (profileId: string) => `export-profiles/${encodeURIComponent(profileId)}/schedules`,
    /** GET — cross-profile list (`profileId`, `status`). */
    list: () => 'export-schedules',
    /** GET — one schedule; PATCH — partial update; DELETE — 204. */
    detail: (id: string) => `export-schedules/${encodeURIComponent(id)}`,
    /** POST — stop future firings, keeping the schedule and its history. */
    pause: (id: string) => `export-schedules/${encodeURIComponent(id)}/pause`,
    /** POST — resume a paused schedule. */
    resume: (id: string) => `export-schedules/${encodeURIComponent(id)}/resume`,
    /** POST — Run now for this schedule (202 `ExportProfileRunResponse`, firing trigger FIRE_NOW). */
    trigger: (id: string) => `export-schedules/${encodeURIComponent(id)}/trigger`,
    /** GET — firing history, newest first, keyset (`limit`, `cursor`). */
    firings: (id: string) => `export-schedules/${encodeURIComponent(id)}/firings`,
    /** POST — next run times (≤5) for `{cadence, timezone}`; writes nothing. */
    preview: () => 'export-schedules/preview',
    /** GET — counts, last-24h firings, the next 5 firings and what needs attention. */
    overview: () => 'export-schedules/overview',
    /** GET — every firing across the subsidiary, newest first, keyset (`limit`, `cursor`,
     *  `profileId`, `scheduleId`, `outcome` RAN|SKIPPED, `since`). */
    firingFeed: () => 'export-schedule-firings',
  },
  /**
   * Transformed downloads — agentic-integrations (`integrationsClient`). The POST
   * records a `TransformerRun` and triggers export-service with the inline
   * definition; the webapp then tracks the returned `exportId` with the existing
   * export-service status / SSE stream (`exports.*`). 422 `NO_TRANSFORMER` = the
   * subsidiary has no default for the connector → use the standard export.
   */
  transformerExports: {
    /** POST — start a transformed export (202 TransformerRun); GET — runs, newest first. */
    list: () => 'transformer-exports',
  },
  /**
   * Export templates + preview — served by **agentic-export-service**, NOT by
   * workflow-webapp, so these use `exportClient` (prefix `/api/v1/export`) and
   * NOT `experimentsClient`.
   *
   * That split is not cosmetic. platform-gateway sends `/workflow/**` to
   * workflow-webapp (`workflow_routes.py`), which is what serves the export
   * lifecycle above; agentic-export-service is fronted only at
   * `/api/v1/export/**`, forwarded with its prefix re-added
   * (`export_service_routes.py:68`). Sending these two through
   * `experimentsClient` reaches Java's `GET /api/v1/exports/{exportId}` with
   * `exportId="templates"` — a `400` on the UUID parse, or a `405` for the
   * POST.
   *
   * Hence the doubled `v1`: the service's exports router declares its own
   * absolute `/v1/exports` prefix and is mounted BOTH bare and under the
   * service's `api_v1_prefix`, so the gateway-reachable wire path is
   * `/api/v1/export/v1/exports/**` (deployed in agentic-export-service #36).
   * The bare `/v1/exports/**` tree still exists on the service but no gateway
   * route fronts it.
   */
  exportTemplates: {
    /**
     * GET — the static export-templates config (connectors, their selectable
     * columns, templates, transformations, file formats). Tenant-independent
     * and version-stamped: the same payload for every caller, so it is cached
     * for the session.
     */
    config: () => 'v1/exports/templates',
    /**
     * POST — render the first N products of a run raw and template-encoded.
     * Body is the trigger body plus `rows`. Tenant-scoped and reads the
     * enrichment table, but starts no workflow and writes nothing.
     */
    preview: () => 'v1/exports/templates/preview',
    /**
     * POST — one keyset page of a run's products, raw and template-encoded.
     * The preview's sibling: that one caps at 50 rows for a picker, this pages
     * the whole run for the full-page export view, and carries the total and
     * the next cursor a paginator needs.
     */
    browse: () => 'v1/exports/templates/browse',
    /** GET — counts over the same config. Not used by the UI today. */
    count: () => 'v1/exports/templates/count',
  },
  /**
   * Catalog cost estimates (`workflow-webapp`, via `workflowRunsClient`) — the
   * pre-enrichment cost gate a full-catalog run records after curation. Keyed
   * on the run's workflow id; encoded so timestamp-bearing ids stay path-safe.
   */
  costEstimates: {
    /** GET — estimates filtered by `?status=&tier=`; approvers see every
     *  subsidiary, everyone else only their own. */
    list: () => 'cost-estimates',
    /** GET — `{ approver }` for the caller (env-configured approver emails). */
    approverStatus: () => 'cost-estimates/approver-status',
    /** GET — decided estimates (`?status=APPROVED,REJECTED,…&tier=&page=&size=`), newest
     *  decision first; same visibility as `list`. */
    history: () => 'cost-estimates/history',
    /** GET — per-product cost of each run option (`?subsidiaryId=&connectorCode=`), from what
     *  recent runs actually spent. */
    rates: () => 'cost-estimates/rates',
    /** Literal segments the backend maps before `{workflowId}` — an id equal to one of them
     *  would read that route instead, so callers must not send it to `byWorkflow`. */
    reservedIds: ['history', 'approver-status', 'rates'] as readonly string[],
    /** GET — latest estimate for the run (404 when the gate never ran). */
    byWorkflow: (workflowId: string) => `cost-estimates/${encodeURIComponent(workflowId)}`,
    /** POST — approve / reject a PENDING_APPROVAL estimate (403 non-approver, 409 not pending). */
    decision: (workflowId: string) => `cost-estimates/${encodeURIComponent(workflowId)}/decision`,
    /** GET — actual LLM spend recorded so far (204 when nothing recorded yet). */
    actual: (workflowId: string) => `cost-estimates/${encodeURIComponent(workflowId)}/actual`,
  },
  /** Run estimates that are not cost (workflow-service, via `workflowRunsClient`). */
  runEstimates: {
    /** GET — start → delivery-ready time range for a run
     *  (`?subsidiaryId=&connectorCode=&products=&scoringPhases=`),
     *  from the subsidiary's (else the platform's) recent enrichment throughput. */
    time: () => 'run-estimates/time',
  },
  /**
   * Workflow execution-tracking (`workflow-webapp`, via `workflowRunsClient`).
   * Read + control API for Temporal runs, tenant-scoped by the JWT
   * `subsidiaryId` claim. `runId` is the human-readable root workflowId (e.g.
   * `DEV-catalog-ops-20260728082601676`); `stage` is a Stage enum value. Both
   * are encoded so timestamp/colon-bearing ids stay path-safe. List/filter and
   * pagination params are passed as Ky `searchParams` in the service layer, not
   * baked in here.
   */
  workflowRuns: {
    /** GET — dashboard list of FLOW-level runs. Filters: `flowType`, `status`. */
    list: () => 'workflow-runs',
    /** GET — run detail: reconciled summary header + stage checklist, plus `invalidItemsRetry`
     *  (the Re-enrich gate) and `repairAction` (the Fix automatically gate). */
    detail: (runId: string) => `workflow-runs/${encodeURIComponent(runId)}`,
    /** GET — live "N of M" counter for the current stage. Poll while `source==LIVE`. */
    liveProgress: (runId: string) => `workflow-runs/${encodeURIComponent(runId)}/live-progress`,
    /** GET — stage drill-down: the batch / sub-batch child nodes for one stage.
     *  Node products reached via `?batchExecutionId=<node.executionId>`. */
    stage: (runId: string, stage: string) =>
      `workflow-runs/${encodeURIComponent(runId)}/stages/${encodeURIComponent(stage)}`,
    /** GET — full nested execution hierarchy (FLOW→COORDINATOR→BATCH→SUB_BATCH). */
    tree: (runId: string) => `workflow-runs/${encodeURIComponent(runId)}/tree`,
    /** GET — per-product table / failed pick-list (keyset paginated). Query:
     *  `stage` (de-facto required), `status`, `batchExecutionId`, `cursor`, `size`≤500. */
    products: (runId: string) => `workflow-runs/${encodeURIComponent(runId)}/products`,
    /** GET — one product's journey (every attempt across stages) + provenance. */
    productJourney: (runId: string, productId: string) =>
      `workflow-runs/${encodeURIComponent(runId)}/products/${encodeURIComponent(productId)}`,
    /** GET — data-lineage graph: `assets[]` (nodes) + `links[]` (edges). */
    lineage: (runId: string) => `workflow-runs/${encodeURIComponent(runId)}/lineage`,
    /** GET — audit timeline of all lifecycle + control actions (asc by occurredAt). */
    controlHistory: (runId: string) => `workflow-runs/${encodeURIComponent(runId)}/control-history`,
    /** GET — shadow-parity report (ops/QA cutover gate): matches/comparable + diffs. */
    parity: (runId: string) => `workflow-runs/${encodeURIComponent(runId)}/parity`,
    /** POST — hold the run at the next stage boundary. Body: `ControlActionRequest`. */
    pause: (runId: string) => `workflow-runs/${encodeURIComponent(runId)}/pause`,
    /** POST — release a paused run. Body: `ControlActionRequest`. */
    resume: (runId: string) => `workflow-runs/${encodeURIComponent(runId)}/resume`,
    /** POST — force-close a run. Body: `ControlActionRequest`. */
    terminate: (runId: string) => `workflow-runs/${encodeURIComponent(runId)}/terminate`,
    /** POST — decide a stage that is AWAITING_GATE (APPROVE/REJECT/JUMP_BACK).
     *  Body: `GateDecisionRequest`. Only wired for CATALOG_OPERATIONS + SCHEDULED_PIPELINE. */
    gate: (runId: string, stage: string) =>
      `workflow-runs/${encodeURIComponent(runId)}/stages/${encodeURIComponent(stage)}/gate`,
    /** POST — retry. Body: `RetryRequest`, scope STAGE | FAILED_ITEMS | INVALID_ITEMS (BATCH →
     *  501); INVALID_ITEMS (stage ENRICHMENT, no `productIds`) re-enriches the flagged set. */
    retry: (runId: string) => `workflow-runs/${encodeURIComponent(runId)}/retry`,
    /** POST — "Fix automatically": write the validation's code repairs onto the run's enriched
     *  rows, then re-validate them. Body (optional) `{ reason?, requestedBy? }`. */
    repair: (runId: string) => `workflow-runs/${encodeURIComponent(runId)}/repair`,
    /** POST — hard-restart enrichment under the same id: terminate the parent and
     *  resume only the not-yet-enriched products (optional config override, e.g.
     *  raised parallelism). Body: `RestartEnrichmentRequest`. `runId` is the top
     *  parent pipeline id, NOT the `…-enrich-coord` id. */
    enrichmentRestart: (runId: string) =>
      `workflow-runs/${encodeURIComponent(runId)}/enrichment/restart`,
  },
  /**
   * Agent APIs, reached through platform-gateway's wildcard agent proxy (use
   * `agentsClient`, prefix `/agents`). The path after `/agents/{agent}/` is forwarded
   * to that agent's Cloud Run service unchanged, so these mirror the agent's own
   * route table 1:1 — see `agents/data_scoring/router.py` in agent-services.
   *
   * All four prompt routes are read-only GETs with no body. Shapes come from
   * `shared/models/enrichment/prompts.py`; responses are bare DTOs with no
   * `{ success, data }` envelope and no `.data` hop.
   */
  agents: {
    /**
     * data-scoring-agent's prompt-exposure routes — the catalog behind
     * Configuration → Prompts. The unit is a prompt **namespace** (a directory under
     * `prompts/`), not an agent: data-scoring renders several, one per vertical, so
     * `agent` repeats across namespaces and is a grouping label rather than a key.
     */
    prompts: {
      /** GET — every namespace with its prompt/template counts. Stats only, no content. */
      namespaces: () => `${DATA_SCORING_AGENT}/prompts/agents`,
      /** GET — one namespace's prompts + Jinja2 template filenames. 404 (with the valid
       *  namespace list in `detail`) for an unknown namespace. */
      namespace: (namespace: string) =>
        `${DATA_SCORING_AGENT}/prompts/agents/${encodeURIComponent(namespace)}`,
      /** GET — every category-level prompt flattened across namespaces. The one call
       *  that enumerates all selectable prompts; templates-only namespaces contribute
       *  no rows. */
      categories: () => `${DATA_SCORING_AGENT}/prompts/categories`,
      /** GET — one prompt's full text (up to ~98k characters, so fetch on selection
       *  only, never for a listing). With `?download=true` the same path answers
       *  `text/plain` plus `Content-Disposition: attachment` instead of the JSON
       *  model — pass that as Ky `searchParams` from the service layer. */
      raw: (namespace: string, key: string) =>
        `${DATA_SCORING_AGENT}/prompts/raw/${encodeURIComponent(namespace)}/${encodeURIComponent(key)}`,
    },
    /**
     * POST — render the prompt an agent WOULD send for a batch of product bodies, with no
     * model call. Per-agent, not data-scoring: the renderer is the agent's own context
     * assembly, so the path takes whichever agent you want the prompt of.
     *
     * Wired on EVERY agent (agent-services #343). `path` defaults to the shared
     * `/internal/render-prompt`; data-scoring overrides it because it runs two scorers with
     * different request models and so exposes two platform-named routes
     * (`/google/internal/render-prompt`, `/meta/internal/render-prompt`) — one route there
     * could only ever show one of the two.
     *
     * The `supported: false` answer still exists in the response model and is still
     * surfaced: it is what the next agent added will return before it has a renderer.
     */
    renderPrompt: (agent: string, path = 'internal/render-prompt') => `${agent}/${path}`,
    /**
     * POST — classify products into a vertical. google-agent only.
     *
     * Costs ONE nano-tier model call per uncached product, which is why it is a separate,
     * explicit action rather than something the prompt preview does for you: the preview's
     * whole guarantee is that it calls no model. `refresh: true` skips the cache read.
     */
    detectCategory: () => `google-agent/internal/detect-category`,
    /**
     * GET — the GMC attribute catalogue, grouped, straight from the agent's own
     * `shared/catalog/gmc_attributes.py`. This is what `requested_fields` may contain, so
     * the picker offers exactly the keys the agent accepts rather than a hardcoded copy
     * that drifts the first time a field is added.
     */
    catalogAttributes: () => `google-agent/catalog/attributes`,
    /**
     * GET — the Meta attribute catalogue, grouped, from the meta agent's own
     * `shared/catalog/meta_attributes.py` (agent-services #402). A DISCLOSURE list, not a
     * selection list: Meta has no `requested_fields`, so `selectable` is false on every
     * catalog field and true only on the two `enrichment_config` post-steps. Pass
     * `include_pass_through=false` (searchParams) to drop the 10 untouched fields.
     */
    metaCatalogAttributes: () => `meta-agent/catalog/attributes`,
    /**
     * GET — the curated content-attribute taxonomy for the enrichment-run picker, from
     * the google agent's own `shared/catalog/gmc_attributes.py`. Replaces
     * `config-api/content-attributes`, which was never database-driven.
     *
     * A different GROUPING from `catalogAttributes()`, not a different catalog: that one
     * is all 44 feed fields in groups A–J for the `requested_fields` picker; this is the
     * 20 curated entries in four product groups. Pass `selectable_only=false`
     * (searchParams) to include the fields that are curated but not offered.
     */
    googleContentAttributes: () => `google-agent/catalog/content-attributes`,
    /**
     * GET — every retailer-specific prompt, `[{ retailer, buckets: [{ bucket, versions }] }]`,
     * from `prompts/google_agent/retailer-configs/` (agent-services #448). `retailer` is the
     * slug to send verbatim as `retailer` on a product body; `[]` when none ship.
     */
    googleRetailerConfigs: () => `google-agent/retailer-configs`,
    /**
     * GET — the locales google-agent translates enriched GMC content into,
     * `[{ code, display_name, is_translation }]` (agent-services #449). `en-US` is the source and
     * is never listed. `is_translation: false` is en-GB — UK spelling on the same English.
     */
    googleTranslationLocales: () => `google-agent/catalog/locales`,
    /**
     * POST — run enrichment on a batch of products. THIS SPENDS MODEL CALLS, unlike
     * `renderPrompt`.
     *
     * `batch_id` in the body is the idempotency key: re-posting the same id resumes from the
     * Redis checkpoint and does not re-run products that already succeeded. Never recycle one
     * across different payloads.
     */
    batchGenerate: (agent: string) => `${agent}/batch-generate`,
    /**
     * GET — progress of one `batch_id`, read from the checkpoint the batch wrote.
     *
     * Registered as a twin of each batch route, so the path must match the batch route the
     * work was posted to — a status read is scoped to its own route's checkpoint namespace and
     * cannot see a sibling route's batches. `include_results=true` attaches each product's
     * full response; leave it off while polling for progress.
     */
    batchGenerateStatus: (agent: string, batchId: string) =>
      `${agent}/batch-generate/status/${encodeURIComponent(batchId)}`,
  },
  /**
   * Content service — AI content generation over a product catalog.
   * Base prefix `/api/v1/content-service` lives on `contentClient`, so every
   * path here is relative to it.
   *
   * Phase 1 covers generation runs + their generated content and failures;
   * attribution, gap analysis, model configuration and prompts land in later
   * phases and are added here as they ship.
   */
  content: {
    /** Liveness/readiness — unauthenticated on the service, useful for an ops banner. */
    health: () => 'health/ready',
    runs: {
      /** GET (keyset page, newest first) · POST (start a run, 201). */
      list: () => 'runs',
      create: () => 'runs',
      /** Stored row + live Temporal progress while the run is in flight. */
      detail: (runId: string) => `runs/${encodeURIComponent(runId)}`,
      pause: (runId: string) => `runs/${encodeURIComponent(runId)}/pause`,
      resume: (runId: string) => `runs/${encodeURIComponent(runId)}/resume`,
      cancel: (runId: string) => `runs/${encodeURIComponent(runId)}/cancel`,
      /** POST — hides a finished run from `GET /runs` (409 while it still runs); nothing is deleted. */
      archive: (runId: string) => `runs/${encodeURIComponent(runId)}/archive`,
      /** POST — shows an archived run in the list again. */
      unarchive: (runId: string) => `runs/${encodeURIComponent(runId)}/unarchive`,
      /** POST — archives (or with `archived: false` unarchives) up to 500 runs; one outcome per id. */
      archiveBulk: () => 'runs/archive',
      /** Ascending by batch index — execution order. Cursor is the last index seen. */
      batches: (runId: string) => `runs/${encodeURIComponent(runId)}/batches`,
      metrics: (runId: string) => `runs/${encodeURIComponent(runId)}/metrics`,
      /** Newest generation per (productId, field). */
      content: (runId: string) => `runs/${encodeURIComponent(runId)}/content`,
      /** Every failed attempt; retries retained rather than collapsed. */
      failures: (runId: string) => `runs/${encodeURIComponent(runId)}/failures`,
      /** R9 — the run's recorded events, newest first; `type` repeatable (exact names), paged. */
      events: (runId: string) => `runs/${encodeURIComponent(runId)}/events`,
      /** Progress per (platform, flow, phase) plus batches by status — stored rows only. */
      stages: (runId: string) => `runs/${encodeURIComponent(runId)}/stages`,
      /** One batch: its stages, their sub-batches, and the live view while it runs. */
      batch: (runId: string, batchIndex: number) =>
        `runs/${encodeURIComponent(runId)}/batches/${String(batchIndex)}`,
      /** The products one batch fetched, with their outcome once it closes (page/size, real total). */
      batchProducts: (runId: string, batchIndex: number) =>
        `runs/${encodeURIComponent(runId)}/batches/${String(batchIndex)}/products`,
      /** What the run did to each product (keyset); `vertical` and `status` are repeatable. */
      products: (runId: string) => `runs/${encodeURIComponent(runId)}/products`,
      /** The run's products counted by (vertical, status), under the list's filters. */
      productCounts: (runId: string) => `runs/${encodeURIComponent(runId)}/products/counts`,
    },
    /**
     * Attribution — what detection decided and what extraction pulled out.
     *
     * The product-scoped reads take no `runId`: attributes are computed once per
     * product and REUSED across runs, so these answer "what do we currently
     * know", not "what did one run write". The run-scoped pair under
     * `content.runs` answers the latter.
     */
    attribution: {
      product: (productId: string) => `attribution/products/${encodeURIComponent(productId)}`,
      productTypes: () => 'attribution/product-types',
      attributes: () => 'attribution/attributes',
      /** Roll-up across the grounding ladder; `rejectedValues` is the curation queue. */
      grounding: () => 'attribution/grounding',
      /**
       * Only what THIS run wrote — empty means the run reused every detection. Paged
       * (`page`/`limit`); `include=review` adds QC/review counts and the review filters/sort.
       */
      runProductTypes: (runId: string) => `runs/${encodeURIComponent(runId)}/product-types`,
      runAttributes: (runId: string) => `runs/${encodeURIComponent(runId)}/attributes`,
      /** POST — append reviewer decisions (≤500 per request); the reviewer comes from the token. */
      reviews: () => 'attribution/reviews',
      /** GET — every review decision on one product, newest first. */
      productReviews: (productId: string) =>
        `attribution/products/${encodeURIComponent(productId)}/reviews`,
    },
    /** Attribution QC — what the service's own rule checks found after a run. */
    qc: {
      /** Paged findings (`page`/`size` ≤500), filterable by productId/severity/subject/checkName. */
      findings: (runId: string) => `runs/${encodeURIComponent(runId)}/qc-findings`,
      /** Findings and affected products per rule and severity. */
      summary: (runId: string) => `runs/${encodeURIComponent(runId)}/qc-summary`,
      /** The review list's header numbers for the whole job. */
      reviewStats: (runId: string) => `runs/${encodeURIComponent(runId)}/review-stats`,
      /** Every product's latest review decisions for the run, as a streamed CSV. */
      reviewExport: (runId: string) => `runs/${encodeURIComponent(runId)}/review-export`,
    },
    /**
     * Quality scores — rule-based content quality before (PRE) and after (POST) enrichment,
     * one platform at a time. Every run route takes `platform` (422 without it when the run
     * scored several) and, except the product detail, `batchIndex`.
     */
    qualityScores: {
      summary: (runId: string) => `runs/${encodeURIComponent(runId)}/scores/summary`,
      matrix: (runId: string) => `runs/${encodeURIComponent(runId)}/scores/matrix`,
      checks: (runId: string) => `runs/${encodeURIComponent(runId)}/scores/checks`,
      verticals: (runId: string) => `runs/${encodeURIComponent(runId)}/scores/verticals`,
      products: (runId: string) => `runs/${encodeURIComponent(runId)}/scores/products`,
      product: (runId: string, productId: string) =>
        `runs/${encodeURIComponent(runId)}/scores/products/${encodeURIComponent(productId)}`,
      /** Tenant-wide, newest run first; `platform` is required. */
      trends: () => 'scores/trends',
    },
    /**
     * The Overview dashboard's reads across runs. Every one takes a period, `since` / `until`
     * (default the last 30 days); see `docs/api-contracts/overview.md` on the service.
     */
    overview: {
      /** Runs by status, kind and day; products processed; with `platform`, the score trend. */
      dashboard: () => 'dashboard/summary',
      /** Typed / no match / low confidence, products per vertical, attribute coverage. */
      attributionSummary: () => 'attribution/summary',
      /** Per run: products typed and attribute slots answered (the chart's tabs). */
      attributionTrends: () => 'attribution/trends',
      /** Products by their newest detection, filterable and searchable; page/size. */
      products: () => 'attribution/products',
      /** Attributes asked and left empty, with POSSIBLY_MISSED / NOT_IN_DATA; page/size. */
      missing: () => 'attribution/missing',
      /** QC across runs: per severity, per check, per day. */
      qcSummary: () => 'qc/summary',
      /** The findings behind it, OPEN / CONFIRMED / DISMISSED; page/size. */
      qcFindings: () => 'qc/findings',
      /** Where the cross-run review queue stands, and decisions in the period. */
      reviewsSummary: () => 'reviews/summary',
      /** The cross-run review queue; page/size. */
      reviewQueue: () => 'reviews/queue',
    },
    /** Gap analysis — on-demand comparison of a run's output against the catalog. */
    gapAnalyses: {
      /** GET (page/size list) · POST (start one, 202). */
      list: (runId: string) => `runs/${encodeURIComponent(runId)}/gap-analyses`,
      start: (runId: string) => `runs/${encodeURIComponent(runId)}/gap-analyses`,
      report: (runId: string, analysisId: string) =>
        `runs/${encodeURIComponent(runId)}/gap-analyses/${encodeURIComponent(analysisId)}/report`,
      findings: (runId: string, analysisId: string) =>
        `runs/${encodeURIComponent(runId)}/gap-analyses/${encodeURIComponent(analysisId)}/findings`,
    },
    /** Model configuration — which model each flow routes to, and what that costs. */
    modelConfiguration: {
      /** Static flow vocabulary — build a run/publish form from this, never hardcode. */
      flows: () => 'model-configuration/flows',
      /** Every catalog row, including superseded prices and non-representative snapshots. */
      catalog: () => 'model-configuration/catalog',
      /** One row per (vendor, tier): what a flow routed to that tier actually runs. */
      catalogCurrent: () => 'model-configuration/catalog/current',
      /** GET the newest version (`?scope=`) · POST to publish the next one. */
      configuration: () => 'model-configuration',
      /** Subsidiary over platform, tiers resolved. A PREVIEW, not what a run uses. */
      resolved: () => 'model-configuration/resolved',
      /** One published version exactly as written — the run-attribution read. */
      version: (configVersion: number) => `model-configuration/versions/${String(configVersion)}`,
      /** Publishes a complete `*` default from FLOW_SPECS, for a fresh environment. */
      seed: () => 'model-configuration/seed',
      /**
       * DELETE — stops configuring one `(platform, flow)`.
       *
       * Publishes the NEXT version without that row rather than deleting
       * anything: the table is append-only, so earlier versions still read as
       * they did. `platform` is part of the address, not a filter.
       */
      removeFlow: (flow: string) => `model-configuration/flows/${encodeURIComponent(flow)}`,
    },
    /** Enrichment field registry — what a flows run on a channel may ask to write. */
    enrichments: {
      /** GET `?connector=GOOGLE_MERCHANT|META_COMMERCE[&vertical=]` — the channel's fields, in display order. */
      fields: () => 'enrichments/fields',
      /** GET — operator edits recorded against one curation FEED product, newest first; never 404s. */
      attributeEdits: (feedProductId: string) =>
        `enrichments/products/${encodeURIComponent(feedProductId)}/attribute-edits`,
      /** PATCH `{field, value}` — override one enriched attribute (`""`/`[]` clears); a person only. */
      attribute: (feedProductId: string, field: string) =>
        `enrichments/products/${encodeURIComponent(feedProductId)}/attributes/${encodeURIComponent(field)}`,
      /** POST R5 `{action: APPROVE|EXCLUDE|RESET, runRef, fields?}` — one operator decision. */
      decision: (feedProductId: string) =>
        `enrichments/products/${encodeURIComponent(feedProductId)}/decision`,
    },
    /** Prompt catalog — the selectable versions, and one version's body. */
    prompts: {
      list: () => 'prompts',
      detail: (flow: string, version: string) =>
        `prompts/${encodeURIComponent(flow)}/${encodeURIComponent(version)}`,
    },
    /** Taxonomy maintenance jobs — POST starts (202) or joins, GET reads the status. No id: each
     *  job is single-flight per subsidiary, so `X-Subsidiary-ID` is the whole address. */
    taxonomy: {
      /** Brings the vector index in step with the ACTIVE vocabulary. */
      vectorReconcile: () => 'taxonomy/vectors/reconcile',
      /** Grounds already-extracted attributes again; never re-runs extraction. */
      attributeReground: () => 'taxonomy/attributes/reground',
      /** GET — both jobs' history, newest first, from Temporal (keyset `cursor`, repeatable `kind`). */
      jobs: () => 'taxonomy/jobs',
    },
    /**
     * Workflow admin — the operator surface over a run's Temporal execution.
     * `reset` and `terminate` are destructive; see docs/content-generation-service.md.
     */
    workflowAdmin: {
      describe: (runId: string) => `runs/${encodeURIComponent(runId)}/workflow`,
      history: (runId: string) => `runs/${encodeURIComponent(runId)}/history`,
      drift: (runId: string) => `runs/${encodeURIComponent(runId)}/drift`,
      reconcile: (runId: string) => `runs/${encodeURIComponent(runId)}/reconcile`,
      reset: (runId: string) => `runs/${encodeURIComponent(runId)}/reset`,
      terminate: (runId: string) => `runs/${encodeURIComponent(runId)}/terminate`,
    },
  },
  // ----- catalog-v2 -----
  /**
   * Catalog service (`agentic-catalog-service`) — base prefix `/api/v1/catalog-service`
   * lives on `catalogServiceClient`. Backs the new `/catalog-v2/*` screens only; the
   * legacy catalog groups above stay in use until the cutover (docs/catalog-service.md).
   * `runRef` accepts the run uuid OR the workflow id; `runId` takes the uuid.
   */
  catalogService: {
    runs: {
      /** GET — paginated run list (status, connector, flow, archived, …). */
      list: () => 'catalog-runs',
      statusCounts: () => 'catalog-runs/status-counts',
      /** GET — the run list's "Created by" options under the list filters (its own `createdBy` ignored). */
      creators: () => 'catalog-runs/creators',
      summary: () => 'catalog-runs/summary',
      /** GET — failures grouped by stage + reason. */
      failures: () => 'catalog-runs/failures',
      /** GET — up to 50 runs (`ids` repeated) as product-history rows show them (§89). */
      summaries: () => 'catalog-runs/summaries',
      byId: (runId: string) => `catalog-runs/${encodeURIComponent(runId)}`,
      archive: (runId: string) => `catalog-runs/${encodeURIComponent(runId)}/archive`,
      unarchive: (runId: string) => `catalog-runs/${encodeURIComponent(runId)}/unarchive`,
      bulkArchive: () => 'catalog-runs/archive',
      bulkUnarchive: () => 'catalog-runs/unarchive',
      lineage: (runId: string) => `catalog-runs/${encodeURIComponent(runId)}/lineage`,
      lineageSummary: (runId: string) =>
        `catalog-runs/${encodeURIComponent(runId)}/lineage/summary`,
      /** GET — one row per listing with each stage's newest attempt (api-changes §49). */
      lineageListings: (runId: string) =>
        `catalog-runs/${encodeURIComponent(runId)}/lineage/listings`,
      /** GET — newest-attempt issues grouped by (stage, reason) (api-changes §49). */
      lineageIssues: (runId: string) => `catalog-runs/${encodeURIComponent(runId)}/lineage/issues`,
      /** GET — one issue group's listings, keyset (api-changes §49). */
      lineageIssueListings: (runId: string) =>
        `catalog-runs/${encodeURIComponent(runId)}/lineage/issues/listings`,
    },
    /** Start, track, decide and recover a run. Control POSTs take a `correlationId`. */
    operations: {
      triggerOptions: () => 'catalog-operations/trigger-options',
      triggerFilters: () => 'catalog-operations/trigger-filters',
      /** POST — 202 `{ jobId, workflowId, warnings[] }`; 409 while the connector lane is busy. */
      trigger: () => 'catalog-operations/trigger',
      /** GET `?flow=&connectorCode=` — New batch's per-stage median durations (#205, S11, B1). */
      estimate: () => 'catalog-operations/estimate',
      /** POST — per-filter + whole-scope match counts for the New batch form (#205, S11, B3). */
      triggerFilterCounts: () => 'catalog-operations/trigger-filters/count',
      /** POST — top values of one filter key, counted with the other filters applied (#205, S11, B3). */
      triggerFilterValues: () => 'catalog-operations/trigger-filters/values',
      /** GET `?connectorCode=&connectionRef=&size=` — syncs a batch could reuse (#205, S11, B4). */
      syncs: () => 'catalog-operations/syncs',
      progress: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/progress`,
      /** SSE — fetch-based client only (EventSource cannot send headers). */
      progressStream: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/progress/stream`,
      stageAttempts: (runRef: string, stage: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/stages/${encodeURIComponent(stage)}/attempts`,
      /**
       * Finding 13 — `GET ?after&size` (keyset), the newest report per rejected (product,
       * destination), plus the whole run's `reasonCounts` (B1, `feat/attention-reasons-and-delivery-rejections`).
       */
      deliveryRejections: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/delivery/rejections`,
      /**
       * Batch Summary funnel (docs/run-summary-funnel.md §7, decision A — split by
       * responsibility). ① reads catalog-service's own DB only (verdict, cards, funnel line,
       * waterfall); poll every 5s while the run is live. 404/501 on an older deploy means the
       * endpoint isn't shipped yet — the UI falls back to today's Summary, never a broken page.
       */
      funnelSummary: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/funnel/summary`,
      /** ② lazy, on a stage row's "Why" — every reason behind one stage's drop. */
      funnelStageReasons: (runRef: string, stage: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/funnel/stages/${encodeURIComponent(stage)}/reasons`,
      /** ④ lazy, opened on demand; cached once read — live delivery counts + rejections. */
      funnelPublish: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/funnel/publish`,
      /** ⑤ lazy — stored content counts cross-checked against content-gen's own live counts. */
      funnelVerify: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/funnel/verify`,
      /** ⑥ lazy — the previous comparable run's funnel line, for "vs last batch" (needs CS-5). */
      funnelComparison: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/funnel/comparison`,
      /** ⑦ lazy, on stage-page open — full breakdown/reasons/markets/gate for one stage
       *  (`?attempt=` passed as a search param). Design C; 404 until catalog-service ships it. */
      funnelStageDetail: (runRef: string, stage: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/funnel/stages/${encodeURIComponent(stage)}`,
      /** ⑧ lazy, on the stage page's Timeline tab (`?stage=&limit=` as search params). */
      funnelTimeline: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/funnel/timeline`,
      /** ⑨ the mix's Products matrix (`?stage=&outcome=&productIds=&q=&page=&size=` as search
       *  params) — catalog-owned cells only; content-owned stages come back `OWNED_ELSEWHERE`
       *  and the UI fills those from content generation. Built in parallel; 404/501 until shipped. */
      funnelProducts: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/funnel/products`,
      fieldSets: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/field-sets`,
      stageDecision: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/stage-decision`,
      /** GET — §93 the run's stage-error ledger: code, severity, retryable, message, next step. */
      errors: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/errors`,
      /** GET — §95 the run's latest cost estimate (`data: null` when it has none). */
      costEstimate: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/cost-estimate`,
      /** POST — §96 ask again for the estimate of a run held because its estimate failed (409
       *  when the gate is not held for a failed estimate). */
      costEstimateRetry: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/cost-estimate/retry`,
      /** POST `{decision, notes?, mode?}` — §95 approve / reject a held cost gate (403 / 409). */
      costDecision: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/cost-decision`,
      /** GET `?tier=` — §95 cost estimates waiting for a decision, this subsidiary. */
      costEstimatesPending: () => 'catalog-operations/cost-estimates',
      /** GET `?status=&tier=&page=&size=` — §95 decided estimates (APPROVED / REJECTED / EXPIRED /
       *  CANCELLED; none named = all four), newest decision first, this subsidiary. */
      costEstimatesHistory: () => 'catalog-operations/cost-estimates/history',
      /** GET — §95 `{approver}`: whether the caller may decide an APPROVERS-tier gate. */
      costApproverStatus: () => 'catalog-operations/cost-estimates/approver-status',
      failurePoints: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/failure-points`,
      pause: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/pause`,
      resume: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/resume`,
      /** POST — stop a run (replaces legacy terminate / stop-and-archive's stop half). */
      reset: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/reset`,
      retryStage: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/retry-stage`,
      retryItems: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/retry-items`,
      restartFromStage: (runRef: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/restart-from-stage`,
      /** POST — reprocess products as a CHILD run. */
      rerun: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/rerun`,
      productScore: (runRef: string, productId: string) =>
        `catalog-operations/${encodeURIComponent(runRef)}/products/${encodeURIComponent(productId)}/score`,
      /** GET — `[{stage, label, description}]`, the backend's own one-line description per stage
       *  (B1). The run header's "How this flow works" panel prefers these descriptions over its
       *  local copy when available; display NAMES always come from this app's own labels file. */
      stageMetadata: () => 'catalog-operations/stage-metadata',
      /** GET — `?after&size` (keyset, newest first): the run's control log, for batch History
       *  (#160, S9, `wt-cs-b1`). */
      actions: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/actions`,
      /** GET — the run's execution tree (FLOW/COORDINATOR/BATCH/SUB_BATCH), for batch History's
       *  "Runs inside this batch" (#160, S9, `wt-cs-b1`). */
      nodes: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/nodes`,
    },
    /**
     * Schedules. `config` = cadence ONLY; `runConfig` = what a firing runs (filters,
     * maxProducts, actions) — Java's `/config` meant `runConfig`.
     */
    schedules: {
      root: () => 'catalog-operations/schedules',
      overview: () => 'catalog-operations/schedules/overview',
      ribbon: () => 'catalog-operations/schedules/ribbon',
      preview: () => 'catalog-operations/schedules/preview',
      /** GET — every schedule's firings (feed). */
      runs: () => 'catalog-operations/schedules/runs',
      byId: (scheduleId: string) =>
        `catalog-operations/schedules/${encodeURIComponent(scheduleId)}`,
      config: (scheduleId: string) =>
        `catalog-operations/schedules/${encodeURIComponent(scheduleId)}/config`,
      runConfig: (scheduleId: string) =>
        `catalog-operations/schedules/${encodeURIComponent(scheduleId)}/run-config`,
      pause: (scheduleId: string) =>
        `catalog-operations/schedules/${encodeURIComponent(scheduleId)}/pause`,
      resume: (scheduleId: string) =>
        `catalog-operations/schedules/${encodeURIComponent(scheduleId)}/resume`,
      /** POST — fire now. */
      trigger: (scheduleId: string) =>
        `catalog-operations/schedules/${encodeURIComponent(scheduleId)}/trigger`,
      /** GET — one schedule's firing history. */
      firings: (scheduleId: string) =>
        `catalog-operations/schedules/${encodeURIComponent(scheduleId)}/runs`,
      /** GET — every schedule's execution tree (schedule → firings → runs). */
      tree: () => 'catalog-operations/schedules/tree',
      /** GET — one schedule's execution tree. */
      scheduleTree: (scheduleId: string) =>
        `catalog-operations/schedules/${encodeURIComponent(scheduleId)}/tree`,
    },
    /** Curation reads. The curation run id IS the catalog run id. */
    curation: {
      runStats: (runId: string) => `curation-runs/${encodeURIComponent(runId)}/stats`,
      runOutcomes: (runId: string) => `curation-runs/${encodeURIComponent(runId)}/outcomes`,
      runProducts: (runId: string) => `curation-runs/${encodeURIComponent(runId)}/products`,
      runProductsQuery: (runId: string) =>
        `curation-runs/${encodeURIComponent(runId)}/products/query`,
      runSkippedProducts: (runId: string) =>
        `curation-runs/${encodeURIComponent(runId)}/skipped-products`,
      runAlreadyCurated: (runId: string) =>
        `curation-runs/${encodeURIComponent(runId)}/already-curated`,
      runNeedsAttention: (runId: string) =>
        `curation-runs/${encodeURIComponent(runId)}/needs-attention`,
      filters: () => 'curation-filters',
      alreadyCuratedRows: () => 'already-curated/rows',
      /** Legacy-only routes with no newer equivalent (the contract says "use this one"). */
      legacyProductVersions: (feedProductId: string) =>
        `curation/products/${encodeURIComponent(feedProductId)}/versions`,
      legacyProductVersion: (feedProductId: string, version: string | number) =>
        `curation/products/${encodeURIComponent(feedProductId)}/versions/${encodeURIComponent(String(version))}`,
      legacyInternalProductVersions: (productId: string) =>
        `curation/internal-products/${encodeURIComponent(productId)}/versions`,
      legacyBySelection: () => 'curation/already-curated/by-selection',
      legacyBySelectionStatus: (jobId: string) =>
        `curation/already-curated/by-selection/${encodeURIComponent(jobId)}`,
    },
    needsAttention: {
      list: () => 'needs-attention',
      labels: () => 'needs-attention/labels',
      /** GET — whole-queue reason counts (images branch, in review). */
      counts: () => 'needs-attention/counts',
      /**
       * Subsidiary-wide feed-product totals. Finding 7 — gains `search` / `labelCode` / the
       * curation-filter set so `needsAttention` matches a filtered queue (B1,
       * `feat/attention-reasons-and-delivery-rejections`); `total` stays the unfiltered
       * catalogue size by design. Same route as before — an older deploy just ignores the
       * extra params.
       */
      feedProductStats: () => 'feed-product-stats',
      /** POST — flag or clear products with no run. */
      update: () => 'feed-products/needs-attention',
    },
    feedProducts: {
      byId: (feedProductId: string) => `feed-products/${encodeURIComponent(feedProductId)}`,
      images: (feedProductId: string) =>
        `feed-products/${encodeURIComponent(feedProductId)}/images`,
      /** GET — the row as the feed stated it: every typed bronze column + the verbatim record. */
      source: (feedProductId: string) =>
        `feed-products/${encodeURIComponent(feedProductId)}/source`,
      /** POST suppress · DELETE lift. */
      imageSuppression: (feedProductId: string, imageId: string) =>
        `feed-products/${encodeURIComponent(feedProductId)}/images/${encodeURIComponent(imageId)}/suppression`,
    },
    /** Product master + sets + cross-run lineage. List walks with `after` (keyset). */
    products: {
      list: () => 'catalog-products',
      byId: (productId: string) => `catalog-products/${encodeURIComponent(productId)}`,
      /** GET read · PUT declare · DELETE dissolve. */
      set: (productId: string) => `catalog-products/${encodeURIComponent(productId)}/set`,
      lineage: (productId: string) => `catalog-products/${encodeURIComponent(productId)}/lineage`,
      images: (productId: string) => `catalog-products/${encodeURIComponent(productId)}/images`,
      /** GET — the family's variants, keyset + filters + facets (api-changes §48). */
      variants: (productId: string) => `catalog-products/${encodeURIComponent(productId)}/variants`,
    },
    images: {
      health: () => 'images/health',
      failures: () => 'images/failures',
      assetUsages: (contentHash: string) =>
        `images/assets/${encodeURIComponent(contentHash)}/usages`,
      /** POST `{contentHashes[1..200], limitPerHash}` — many hashes' usages (images branch, in review). */
      assetUsagesQuery: () => 'images/usages/query',
    },
    experiments: {
      root: () => 'experiments',
      /** GET — list chip counts in one read (api-changes §49). */
      statusCounts: () => 'experiments/status-counts',
      byId: (experimentId: string) => `experiments/${encodeURIComponent(experimentId)}`,
      bulkArchive: () => 'experiments/archived:batch',
      archived: (experimentId: string) =>
        `experiments/${encodeURIComponent(experimentId)}/archived`,
      /** PUT multipart (`file` + `idType`) · DELETE clear. */
      candidateScope: (experimentId: string) =>
        `experiments/${encodeURIComponent(experimentId)}/candidate-scope`,
      armAssignmentFile: (experimentId: string) =>
        `experiments/${encodeURIComponent(experimentId)}/arm-assignment-file`,
      runs: (experimentId: string) => `experiments/${encodeURIComponent(experimentId)}/runs`,
      progress: (experimentId: string) =>
        `experiments/${encodeURIComponent(experimentId)}/progress`,
      sample: (experimentId: string) => `experiments/${encodeURIComponent(experimentId)}/sample`,
      sampleAttempts: (experimentId: string) =>
        `experiments/${encodeURIComponent(experimentId)}/sample-attempts`,
      pipeline: (experimentId: string) =>
        `experiments/${encodeURIComponent(experimentId)}/pipeline`,
      pipelinePreview: (experimentId: string) =>
        `experiments/${encodeURIComponent(experimentId)}/pipeline/preview`,
      approval: (experimentId: string) =>
        `experiments/${encodeURIComponent(experimentId)}/approval`,
      stop: (experimentId: string) => `experiments/${encodeURIComponent(experimentId)}/stop`,
      /** POST — retry a failed arm split. */
      armAssignment: (experimentId: string) =>
        `experiments/${encodeURIComponent(experimentId)}/arm-assignment`,
      /** GET — which arm each candidate is in (keyset). */
      armAssignments: (experimentId: string) =>
        `experiments/${encodeURIComponent(experimentId)}/arm-assignments`,
    },
  },
  /**
   * Content-generation reads a catalog-v2 run screen composes in (scores, samples,
   * enriched products). Served by content-service on `contentClient`, keyed by the
   * catalog `runRef` — catalog does not copy these results.
   */
  contentCatalogRuns: {
    /**
     * GET — content-generation's live progress for a catalog run (batches, throughput, time
     * left) — summary funnel §7 ⑦. Keyed by the catalog `runRef`, unlike `content.runs.*` below
     * which take content-gen's OWN run id (one of `FunnelStage.contentProgress.generationRunIds`).
     */
    progress: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/progress`,
    /** GET — what a run changed in the feed: added/updated/removed totals, per attribute, top 200. */
    feedAudit: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/feed-audit`,
    /** GET — every changed product, heaviest changers first, page/size ≤ 200. */
    feedAuditProducts: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/feed-audit/products`,
    /** GET — one product's AI-performance drill-down (dimensions, gap attributes, search volume). */
    productPerformance: (runRef: string, productId: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/products/${encodeURIComponent(productId)}/performance`,
    /** GET — R5: the run's operator decisions per feed row (`feedProductIds` ≤ 250, paged). */
    productDecisions: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/product-decisions`,
    /** GET — the APPROVED operator edits this run's delivery must ship (`feedProductIds` ≤ 250). */
    attributeOverrides: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/attribute-overrides`,
    /** GET — scores for ≤100 `productIds`; `missingReasons=true` adds why each missing one has none. */
    scores: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/scores`,
    /** GET — R13: the run's PRE + after sets side by side (headline, tiers, verdict movement). */
    scoresSummary: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/scores/summary`,
    /** GET — R13: every rule on every field, failing before and after (fixed / regressed). */
    scoresChecks: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/scores/checks`,
    /** GET — R13: paged products before → after; `verdict`, `preVerdict`, `order`. */
    scoresProducts: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/scores/products`,
    /** GET — R10: the run's content metrics against the previous batch on its connector. */
    metricsCompare: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/metrics/compare`,
    /** GET — R11: tag / trend / occasion chips BEFORE enrichment for 1–100 `productIds`. */
    productTagsTrendsBefore: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/products/tags-trends/before`,
    scorecard: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/scorecard`,
    samplePreview: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/sample-preview`,
    sampleQualityScore: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/sample-quality-score`,
    enrichedProducts: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/enriched-products`,
    failedProducts: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/failed-products`,
    /** GET — one product's full score breakdown (dimensions, per-field scores + feedback, scored text). */
    scoreDetails: (runRef: string, productId: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/products/${encodeURIComponent(productId)}/score-details`,
    /** GET — read-back for an on-demand re-score started on catalog-service. */
    productScore: (runRef: string, productId: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/products/${encodeURIComponent(productId)}/score`,
    /** GET — Performance Signals cards (popular terms/attributes, occasions, trends), before → after. */
    insights: (runRef: string) => `catalog-operations/${encodeURIComponent(runRef)}/insights`,
    /** GET — the products behind one Performance Signals row (`category` + `label`), paged. */
    insightMembership: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/performance-insights/membership/products`,
    /** GET — the products behind one scorecard row (an attribute `FIELD` or a failing `CHECK`),
     *  grouped and paged; built in parallel on content-generation (404 until deployed). */
    scorecardProducts: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/scorecard/products`,
    /** GET — whether the run's Performance Signals cards are recomputing (always 200; `NONE`). */
    performanceInsightsStatus: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/performance-insights/status`,
    /** POST — recompute the run's Performance Signals cards; answers the computation id to poll. */
    insightsRecompute: (runRef: string) =>
      `catalog-operations/${encodeURIComponent(runRef)}/insights/recompute`,
    /** GET — one recompute's state (`RUNNING` | `COMPLETED` | `FAILED` | `CANCELLED`). */
    insightsRecomputeStatus: (computationId: string) =>
      `catalog-operations/insights/recompute/${encodeURIComponent(computationId)}`,
  },
  // ----- end catalog-v2 -----
  // ----- taxonomy-studio -----
  /**
   * Taxonomy Studio (agentic-config `/taxonomy`, via `configClient`). The versioned Lily
   * product + attribute taxonomy: reads take `release=live|draft|<seq>`, writes land in the
   * single open draft. Contract: agentic-config `docs/taxonomy-studio-api.md`.
   */
  taxonomyStudio: {
    permissions: () => 'taxonomy/me/permissions',
    nodes: () => 'taxonomy/nodes',
    nodesBatch: () => 'taxonomy/nodes:batch',
    node: (id: number) => `taxonomy/nodes/${String(id)}`,
    nodeResolved: (id: number) => `taxonomy/nodes/${String(id)}/resolved`,
    nodeArchivePreview: (id: number) => `taxonomy/nodes/${String(id)}/archive-preview`,
    nodeArchive: (id: number) => `taxonomy/nodes/${String(id)}/archive`,
    nodeRestore: (id: number) => `taxonomy/nodes/${String(id)}/restore`,
    sections: () => 'taxonomy/sections',
    sectionsBatch: () => 'taxonomy/sections:batch',
    section: (key: string) => `taxonomy/sections/${encodeURIComponent(key)}`,
    sectionArchive: (key: string) => `taxonomy/sections/${encodeURIComponent(key)}/archive`,
    sectionRestore: (key: string) => `taxonomy/sections/${encodeURIComponent(key)}/restore`,
    sectionGmcMappings: (key: string) =>
      `taxonomy/sections/${encodeURIComponent(key)}/gmc-mappings`,
    sectionValues: (key: string) => `taxonomy/sections/${encodeURIComponent(key)}/values`,
    sectionValuesBatch: (key: string) =>
      `taxonomy/sections/${encodeURIComponent(key)}/values:batch`,
    sectionValuesMerge: (key: string) =>
      `taxonomy/sections/${encodeURIComponent(key)}/values:merge`,
    sectionValue: (key: string, valueKey: string) =>
      `taxonomy/sections/${encodeURIComponent(key)}/values/${encodeURIComponent(valueKey)}`,
    sectionValueArchive: (key: string, valueKey: string) =>
      `taxonomy/sections/${encodeURIComponent(key)}/values/${encodeURIComponent(valueKey)}/archive`,
    sectionValueRestore: (key: string, valueKey: string) =>
      `taxonomy/sections/${encodeURIComponent(key)}/values/${encodeURIComponent(valueKey)}/restore`,
    sectionValueScope: (key: string, valueKey: string) =>
      `taxonomy/sections/${encodeURIComponent(key)}/values/${encodeURIComponent(valueKey)}/scope`,
    sectionAttachments: (key: string) => `taxonomy/sections/${encodeURIComponent(key)}/attachments`,
    sectionAttachment: (key: string, nodeId: number) =>
      `taxonomy/sections/${encodeURIComponent(key)}/attachments/${String(nodeId)}`,
    sectionAttachmentRestore: (key: string, nodeId: number) =>
      `taxonomy/sections/${encodeURIComponent(key)}/attachments/${String(nodeId)}/restore`,
    releases: () => 'taxonomy/releases',
    release: (seq: string) => `taxonomy/releases/${encodeURIComponent(seq)}`,
    releaseChanges: (seq: string) => `taxonomy/releases/${encodeURIComponent(seq)}/changes`,
    releaseRestorePreview: (seq: number) => `taxonomy/releases/${String(seq)}/restore-preview`,
    releaseRestore: (seq: number) => `taxonomy/releases/${String(seq)}/restore`,
    changes: () => 'taxonomy/changes',
    diff: () => 'taxonomy/diff',
    draft: () => 'taxonomy/draft',
    draftChecks: () => 'taxonomy/draft/checks',
    draftActions: () => 'taxonomy/draft/actions',
    draftActionUndo: (actionId: string) =>
      `taxonomy/draft/actions/${encodeURIComponent(actionId)}/undo`,
    draftChangeRevert: (changeId: string) =>
      `taxonomy/draft/changes/${encodeURIComponent(changeId)}/revert`,
    draftChangesRevert: () => 'taxonomy/draft/changes:revert',
    draftPublish: () => 'taxonomy/draft/publish',
    template: (template: string) => `taxonomy/templates/${encodeURIComponent(template)}.csv`,
    exportCsv: (template: string) => `taxonomy/export/${encodeURIComponent(template)}.csv`,
    uploads: () => 'taxonomy/uploads',
    upload: (id: string) => `taxonomy/uploads/${encodeURIComponent(id)}`,
    uploadRows: (id: string) => `taxonomy/uploads/${encodeURIComponent(id)}/rows`,
    uploadRow: (id: string, rowNo: number) =>
      `taxonomy/uploads/${encodeURIComponent(id)}/rows/${String(rowNo)}`,
    uploadErrorsCsv: (id: string) => `taxonomy/uploads/${encodeURIComponent(id)}/errors.csv`,
    uploadApply: (id: string) => `taxonomy/uploads/${encodeURIComponent(id)}/apply`,
  },
  // ----- end taxonomy-studio -----
} as const;
